import { showToast } from '../lib/toast.js';
import { SETTINGS_SECTION_HANDOVER } from '../lib/vario-route.js';
import { awaitJob } from '../lib/await-job.js';
import { confirmDiscardChanges } from '../lib/tab-dirty.js';
import { infoBadge } from '../lib/info-badge.js';
import { CRON_LABELS, humanizeCronJobname, describeCronSchedule } from '../lib/cron-labels.js';
import { type SettingsCtx, DEFAULT_SHOP, defaultShop, loadChannels, loadShopSettings, loadWarehouses, saveLager, saveImport, saveAnlagepruefung, saveVariantModes, saveShopSettings, enqueueCustomerImport, enqueueCustomerExport, assignChannel, loadShopPublicCreds, saveCredentials, createChannel, loadCustomerGroups, setCustomerGroup, createPriceGroup, createCustomerGroup, loadCountryMap, type CountryRow, enqueueCountrySync, enqueueCarrierSync, type ChannelOption, discoverModalities, loadChannelMappings, setChannelMapping, loadPaymentOptions, loadTaxClassOptions, loadOrderStatusOptions, loadParcelServiceOptions, loadBasePriceUnits, setBasePriceUnitMapping, setQuantityUnitMapping, unitCatalogAdmin, loadManufacturerOptions, getManufacturerMappings, setManufacturerMapping, deleteManufacturerMapping, loadVariantMatrix, createVarioVariant, setVariantOverride, clearVariantOverride, type VariantMatrix, type GambioVariantOption, getProductFieldMappings, setProductFieldMapping, deleteProductFieldMapping, type ProductFieldMappingKind, loadShippingStatusCatalog, saveShippingStatusCatalogEntry, deleteShippingStatusCatalogEntry, type ShippingStatusCatalogEntry, loadCurrencyDiff, activateVarioCurrency, type CurrencyDiffRow, loadOrderBackfillCursor, saveOrderBackfillCursor, loadContactTypeOptions, saveGpsr, loadVariantConflicts, acknowledgeVariantShare, enqueueVariantImageImport, enqueueStockInventory, loadMappingOpenCounts } from './settings/data.js';
import { conflictSentence, type ConflictRow } from '../lib/variant-collision-text.js';
import { attributeChoiceLabel } from '../lib/variant-attribute-label.js';
import { staleValueMappings, type ValueMapping } from '../lib/variant-mapping-consistency.js';
import { openModal, openErrorModal } from '@vario-connect/frontend-design';
import { describeVarioError } from '../lib/vario-error-text.js';

/**
 * Backend-Fehler in den Einstellungen sichtbar machen — Klartext oben, kopierbares JSON unten.
 *
 * Vorher stand hier überall nur ein Toast mit `e.message`. Bei einer VARIO-Antwort war das eine
 * abgeschnittene JSON-Wand, aus der niemand etwas ablesen konnte (Betreiber-Befund 28.08.2026:
 * „Fehler mit abgeschnittener Fehlermeldung"). Der Satz kommt jetzt aus `describeVarioError`, der
 * volle Wortlaut steht im Modal und lässt sich kopieren — wie in den anderen Bereichen auch.
 *
 * Reine EINGABE-Prüfungen („Bitte eine ganze Zahl ab 0 eintragen") bleiben bewusst Toasts: dafür
 * ein Modal mit JSON zu öffnen wäre lauter, nicht klarer.
 */
function zeigeFehler(titel: string, e: unknown, kontext: Record<string, unknown> = {}): void {
  const roh = e instanceof Error ? e.message : String(e);
  const d = describeVarioError(roh);
  openErrorModal({
    title: titel,
    items: [{ message: d.text }],
    ...(d.trackingId ? { intro: `VARIO-Vorgang ${d.trackingId} — diese Kennung hilft dem VARIO-Support.` } : {}),
    context: { bereich: 'einstellungen', ...kontext },
    json: { message: roh, ...(e instanceof Error && e.stack ? { stack: e.stack } : {}) },
  });
}
import { GAMBIO_PRODUCT_TYPES, VARIO_PRODUCT_TYPES, VARIO_STOCK_STATES } from '../lib/gambio-product-field-catalogs.js';
import { gambioCurrencyAdminUrl, gambioCustomerConfigAdminUrl, gambioPaymentMethodsAdminUrl, gambioShippingModulesAdminUrl, gambioTaxRatesAdminUrl, gambioIdStartsAdminUrl } from '../lib/gambio-links.js';
import { varioVariantAdminUrl, varioPaymentModalitiesUrl, varioCarrierAdminUrl, varioDeliveryModalitiesUrl, varioTaxesAdminUrl, varioCountersUrl, varioContactTypesUrl, varioCurrenciesUrl, varioInventoriesUrl } from '../lib/vario-links.js';
import { renderArticleMapping } from './tabs/article-mapping.js';
import { renderAdditionalFieldsAdmin } from './settings/additional-fields-admin.js';
import { viewOptionen } from './settings/options-view.js';
import { renderCategoriesEditor } from './settings/categories-editor.js';
import { helpButtonHtml, wireHelpButtons } from '../lib/help-panel.js';
import { connectionPillHtml, refreshConnectionPills } from '../lib/connection-pill.js';

interface PageCtx {
  contentEl: HTMLElement;
  supabase: SettingsCtx['supabase'];
  session: { token: string; tenantId: string; marketplaceKind: string };
  supabaseUrl: string;
}

type SectionId = 'api' | 'lager' | 'import' | 'anlagepruefung' | 'darstellung' | 'betrieb' | 'kundenexport' | 'kundengruppen' | 'kategorien' | 'varianten' | 'optionen' | 'laender' | 'zahlungsarten' | 'versandarten' | 'paketdienste' | 'steuerklassen' | 'orderstatus' | 'grundpreis' | 'hersteller' | 'herstellerGpsr' | 'produkttyp' | 'lieferstatus' | 'waehrungen' | 'artikelmapping' | 'zusatzfelder' | 'ablaeufe';
interface NavItem { id: SectionId; label: string; icon: string }
const NAV: Array<{ group: string; items: NavItem[] }> = [
  { group: 'Allgemein', items: [
    { id: 'api', label: 'API-Zugang & Kanal', icon: '<rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>' },
    { id: 'lager', label: 'Lager', icon: '<path d="M3 9 12 3l9 6v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 21V12h6v9"/>' },
    { id: 'import', label: 'Import & Produktanlage', icon: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/>' },
    { id: 'anlagepruefung', label: 'Anlage-Prüfung', icon: '<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>' },
    { id: 'darstellung', label: 'Darstellung (Standard)', icon: '<circle cx="12" cy="12" r="3"/><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/>' },
    { id: 'betrieb', label: 'Bestellungen & Sync', icon: '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>' },
    { id: 'kundenexport', label: 'Kunden', icon: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/>' },
  ]},
  { group: 'Mapping Vario ↔ Gambio', items: [
    { id: 'kundengruppen', label: 'Kundengruppen', icon: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>' },
    { id: 'kategorien', label: 'Kategorien', icon: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>' },
    { id: 'varianten', label: 'Varianten', icon: '<rect x="4" y="3" width="6" height="6" rx="1"/><rect x="14" y="15" width="6" height="6" rx="1"/><path d="M7 9v3a2 2 0 0 0 2 2h8"/>' },
    { id: 'optionen', label: 'Optionen', icon: '<rect x="3" y="4" width="18" height="4" rx="1"/><rect x="3" y="10" width="18" height="4" rx="1"/><rect x="3" y="16" width="18" height="4" rx="1"/>' },
    { id: 'laender', label: 'Länder', icon: '<circle cx="12" cy="12" r="9"/><path d="M3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 0 1 0 18"/>' },
    { id: 'zahlungsarten', label: 'Zahlungsarten', icon: '<rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/>' },
    { id: 'versandarten', label: 'Versandarten', icon: '<path d="M14 18V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h2"/><path d="M14 9h4l3 3v5a1 1 0 0 1-1 1h-1"/><circle cx="7.5" cy="18.5" r="1.8"/><circle cx="17.5" cy="18.5" r="1.8"/>' },
    { id: 'paketdienste', label: 'Paketdienste', icon: '<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="M3.3 7 12 12l8.7-5"/><path d="M12 22V12"/>' },
    { id: 'steuerklassen', label: 'Steuerklassen', icon: '<circle cx="12" cy="12" r="9"/><path d="M8 16l8-8"/><circle cx="9.5" cy="9.5" r="1.5"/><circle cx="14.5" cy="14.5" r="1.5"/>' },
    { id: 'orderstatus', label: 'Orderstatus', icon: '<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>' },
    { id: 'grundpreis', label: 'Einheiten', icon: '<path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-6"/>' },
    { id: 'hersteller', label: 'Hersteller', icon: '<path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-6h6v6"/>' },
    { id: 'herstellerGpsr', label: 'Hersteller-GPSR', icon: '<path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-6h6v6"/>' },
    { id: 'produkttyp', label: 'Produkttyp', icon: '<path d="M21 8 12 3 3 8l9 5 9-5z"/><path d="M3 8v8l9 5 9-5V8"/><path d="M12 13v8"/>' },
    { id: 'lieferstatus', label: 'Lieferstatus', icon: '<rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7"/><circle cx="5.5" cy="18.5" r="1.8"/><circle cx="18.5" cy="18.5" r="1.8"/>' },
    { id: 'waehrungen', label: 'Währungen', icon: '<circle cx="12" cy="12" r="9"/><path d="M14.5 9.5a3 3 0 0 0-5 2.2c0 2.8 5 2 5 4.6a3 3 0 0 1-5 2.2"/><path d="M12 6v12"/>' },
    { id: 'artikelmapping', label: 'Artikel-Mapping', icon: '<path d="M4 4h6v6H4z"/><path d="M14 14h6v6h-6z"/><path d="M10 7h4M17 10v4"/>' },
    { id: 'zusatzfelder', label: 'Zusatzfelder', icon: '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9h10M7 13h6"/>' },
  ]},
  { group: 'System', items: [
    { id: 'ablaeufe', label: 'Automatische Abläufe', icon: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>' },
  ]},
];

const HEADER_HTML = `
  <header class="apphead">
    <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce"><img src="/logo-black.png" alt="go eCommerce" /></a>
    <img class="gambio-logo" src="/gambio-logo.png?v=2" alt="Gambio" />
    <span class="hdivider"></span>
    <div class="htitle"><div class="heyebrow">Gambio · Einstellungen</div><h1>Einstellungen ${infoBadge('Grundeinstellungen dieses Gambio-Verkaufskanals.')}<span id="go-conflict-flag"></span></h1></div>
    <div class="hactions">${helpButtonHtml('einstellungen')}${connectionPillHtml()}</div>
  </header>`;

let active: SectionId = 'api';
let lastCtx: SettingsCtx | null = null;
/** Offene Varianten-Attribut-Kollisionen (Migration 0318). Wird bei jedem Seitenaufbau neu geladen;
 *  treibt den Punkt am Menüpunkt „Varianten", den Kopf-Indikator und das Banner im Varianten-Abschnitt. */
let variantConflicts: ConflictRow[] = [];
/** Monotones Token für JEDEN Schreibzugriff auf `variantConflicts`: sonst gewinnt die Antwort, die
 *  zuletzt LANDET, nicht die, die zuletzt GEFRAGT wurde. Mehrere Quittierungen in kurzer Folge erzeugen
 *  überlappende Abfragen; eine verspätete alte Antwort würde die Marker wieder auf „1 Konflikt" setzen,
 *  während das Panel schon ohne Banner gerendert ist — Sackgasse. */
let conflictsSeq = 0;

export function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}


/** Gültige Sektions-IDs für den Deep-Link (`?gambioSettingsSection=…`, z. B. aus dem Order-Import-Fehler). */
const SECTION_IDS: ReadonlySet<string> = new Set(['api', 'lager', 'import', 'darstellung', 'betrieb', 'kundenexport', 'kundengruppen', 'kategorien', 'varianten', 'optionen', 'laender', 'zahlungsarten', 'versandarten', 'paketdienste', 'steuerklassen', 'grundpreis', 'hersteller', 'herstellerGpsr', 'produkttyp', 'lieferstatus', 'artikelmapping', 'zusatzfelder', 'ablaeufe']);

export async function renderSettingsPage(ctx: PageCtx): Promise<void> {
  ctx.contentEl.hidden = false;

  // Deep-Link: eine Ziel-Sektion direkt öffnen — z. B. wenn der Order-Import mangels Versand-/
  // Zahlart-Zuordnung fehlschlägt und von dort verlinkt wird. Zwei Quellen, weil es zwei Wege
  // hierher gibt: der Query-Parameter (die App navigiert selbst) und die sessionStorage-Übergabe
  // (VARIO wechselt den Reiter und setzt die iframe-URL dabei selbst — unser Parameter überlebt
  // das nicht). Die Übergabe wird nach dem Lesen entfernt, sonst springt jeder spätere Aufruf
  // der Einstellungen wieder in dieselbe Sektion.
  let target = new URL(window.location.href).searchParams.get('gambioSettingsSection');
  try {
    const handover = sessionStorage.getItem(SETTINGS_SECTION_HANDOVER);
    if (handover) {
      sessionStorage.removeItem(SETTINGS_SECTION_HANDOVER);
      if (!target) target = handover;
    }
  } catch {
    /* privater Modus o. Ä. — dann bleibt es beim Query-Parameter */
  }
  if (target && SECTION_IDS.has(target)) active = target as SectionId;
  document.documentElement.style.height = '100%';
  document.body.style.height = '100%';
  const appEl = document.getElementById('app');
  if (appEl) appEl.style.height = '100%';
  ctx.contentEl.style.height = '100%';
  ctx.contentEl.classList.add('go-settings', 'go-settings-fullpage');

  // Header + work split DIRECTLY in contentEl (no .main wrapper - avoids double padding).
  ctx.contentEl.innerHTML = `
    ${HEADER_HTML}
    <div class="work">
      <nav class="nav" id="go-nav"></nav>
      <section class="panel"><div class="panel-head" id="go-phead"></div><div class="panel-body" id="go-pbody"></div></section>
    </div>`;

  wireHelpButtons(ctx.contentEl);

  const sctx: SettingsCtx = { supabase: ctx.supabase, session: { token: ctx.session.token, tenantId: ctx.session.tenantId }, supabaseUrl: ctx.supabaseUrl };
  renderNav(ctx.contentEl);
  refreshOpenCounts(ctx.contentEl, sctx);
  renderConflictFlag(ctx.contentEl);
  refreshVariantConflicts(ctx.contentEl, sctx);
  await renderPanel(ctx.contentEl, sctx);
}

/** Zieht den Marker am Menüpunkt „Varianten" und den Kopf-Indikator nach, sobald die Konflikt-Liste da
 *  ist. Bewusst NICHT awaited: ein langsamer RPC darf weder die Navigation leer stehen lassen noch einen
 *  Abschnittswechsel verzögern. Kann nicht rejecten (siehe `loadVariantConflicts`).
 *  Nur für Abschnitte AUSSER „Varianten" — dort lädt das `Promise.all` in `viewVarianten` die Liste, ein
 *  zweiter Aufruf wäre ein unnötiger RPC und ein zweiter Schreiber auf `variantConflicts`. */
function refreshVariantConflicts(host: HTMLElement, sctx: SettingsCtx): void {
  if (active === 'varianten') return;
  const seq = ++conflictsSeq;
  void loadVariantConflicts(sctx, DEFAULT_SHOP).then((rows) => {
    if (seq !== conflictsSeq) return; // veraltete Antwort verwerfen
    variantConflicts = rows;
    renderNav(host);
    renderConflictFlag(host);
  });
}

/** Kopf-Indikator neben der H1 — macht die offene Aufgabe unabhängig vom aktiven Abschnitt sichtbar.
 *  Klick springt in den Varianten-Abschnitt (kein Modal — das öffnet nur der „Details"-Button dort). */
function renderConflictFlag(host: HTMLElement): void {
  const slot = host.querySelector<HTMLElement>('#go-conflict-flag');
  if (!slot) return;
  const n = variantConflicts.length;
  if (n === 0) { slot.innerHTML = ''; return; }
  const label = n === 1 ? '1 Varianten-Konflikt' : `${n} Varianten-Konflikte`;
  slot.innerHTML = `<button type="button" id="go-conflict-flag-btn" title="Benutzereingriff nötig"
    style="margin-left:10px;vertical-align:middle;cursor:pointer;display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:600;border-radius:999px;padding:4px 11px;background:#FDF1DC;color:#9A6A12;border:1px solid #F5DFB0;">
    <span style="width:6px;height:6px;border-radius:50%;background:var(--warn);"></span>${escHtml(label)}</button>`;
  const btn = slot.querySelector<HTMLButtonElement>('#go-conflict-flag-btn');
  if (btn) btn.onclick = () => guardedSectionSwitch(() => { active = 'varianten'; void rerender(host); });
}

/**
 * Offene Zuordnungen je Abschnitt, Grundlage für die rote Pille in der Navigation.
 *
 * Zwei Quellen, absichtlich: (1) `gambio_mapping_open_counts` liefert beim Öffnen der Seite in
 * EINEM Aufruf alles, was in der Datenbank steht. (2) Jeder Abschnitt, der beim Anzeigen ohnehin
 * „x/y zugeordnet“ ausrechnet, meldet seine Zahl über `setOpenCount` nach — das deckt die
 * Abschnitte ab, deren Gegenseite nur live in Gambio/VARIO existiert (Einheiten, Hersteller,
 * Varianten). Ein fehlender Eintrag heißt „noch nicht ermittelt“ und zeigt KEINE Pille;
 * eine 0 heißt „nichts offen“.
 */
const openCounts: Partial<Record<SectionId, number>> = {};

/** Zahl eines Abschnitts setzen und die Navigation nachziehen, wenn sie sich geändert hat. */
function setOpenCount(host: HTMLElement, section: SectionId, open: number): void {
  if (openCounts[section] === open) return;
  openCounts[section] = open;
  renderNav(host);
}

/** Einmal beim Seitenaufbau: die billigen Zahlen aus der Datenbank holen. Nicht awaited — eine
 *  langsame RPC darf die Navigation nicht aufhalten; ein Fehler lässt die Pillen einfach weg. */
function refreshOpenCounts(host: HTMLElement, sctx: SettingsCtx): void {
  void loadMappingOpenCounts(sctx, DEFAULT_SHOP)
    .then((counts) => {
      let changed = false;
      for (const [section, open] of Object.entries(counts)) {
        const id = section as SectionId;
        if (openCounts[id] === open) continue;
        openCounts[id] = open;
        changed = true;
      }
      if (changed) renderNav(host);
    })
    .catch((e: Error) => console.warn('[settings] offene Zuordnungen nicht ermittelbar:', e.message));
}

function renderNav(host: HTMLElement): void {
  const nav = host.querySelector<HTMLElement>('#go-nav')!;
  nav.innerHTML = NAV.map((grp) => `
    <div class="nav-group"><div class="nav-glabel">${grp.group}</div>
      ${[...grp.items].sort((a, b) => a.label.localeCompare(b.label, 'de')).map((it) => `
        <div class="nav-item ${active === it.id ? 'active' : ''}" data-nav="${it.id}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">${it.icon}</svg>
          <span class="nlabel">${it.label}</span>
          ${it.id === 'varianten' && variantConflicts.length > 0
            ? `<span class="badge warn" title="Benutzereingriff nötig" aria-label="${variantConflicts.length} offene Varianten-Konflikte">${variantConflicts.length}</span>`
            : ''}
          ${(openCounts[it.id] ?? 0) > 0
            ? `<span class="badge miss" title="Hier fehlen noch Zuordnungen" aria-label="${openCounts[it.id]} offene Zuordnungen">${openCounts[it.id]}</span>`
            : ''}
        </div>`).join('')}
    </div>`).join('');
  nav.querySelectorAll<HTMLElement>('[data-nav]').forEach((el) => {
    el.onclick = () => guardedSectionSwitch(() => {
      active = el.dataset['nav'] as SectionId;
      void rerender(host);
    });
  });
}

async function rerender(host: HTMLElement): Promise<void> {
  renderNav(host);
  renderConflictFlag(host);
  // Konflikt-Marker nachziehen (nicht blockierend) — nach dem Quittieren verschwindet er so ohne
  // manuelles Neuladen, ohne dass ein langsamer RPC den Abschnittswechsel aufhält.
  if (lastCtx) refreshVariantConflicts(host, lastCtx);
  if (lastCtx) await renderPanel(host, lastCtx);
}

// Generische Maptable: links Vario, rechts Gambio (editierbar oder Label) + Status-Chip.
/**
 * Tabellenrahmen der Zuordnungen. `vHead2` splittet die Vario-Seite in ZWEI Spalten mit eigener
 * Überschrift — bei den Zahlungs- und Versandarten stehen dort zwei Dropdowns nebeneinander
 * (Art + Bedingung), und eine gemeinsame Überschrift „Vario-Zahlart · Zahlungsbedingung" ließ
 * offen, welches Dropdown welches ist (Hinweis des Betreibers 2026-08-24). Die Zeilen liefern
 * dann entsprechend ZWEI `<td>`.
 */
function maptableShell(vHead: string, gHead: string, rowsHtml: string, hasAction = false, vHead2?: string): string {
  const actTh = hasAction ? '<th class="act-cell"></th>' : '';
  const secondTh = vHead2 ? `<th>${vHead2}</th>` : '';
  const cols = (hasAction ? 5 : 4) + (vHead2 ? 1 : 0);
  return `<table class="maptable"><thead><tr><th>${vHead}</th>${secondTh}<th class="arrow"></th><th>${gHead}</th><th class="st-cell">Status</th>${actTh}</tr></thead><tbody>${rowsHtml || `<tr><td colspan="${cols}" style="text-align:center;color:#aab;padding:26px;">Keine Einträge.</td></tr>`}</tbody></table>`;
}
function arrowCell(): string { return `<td class="arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg></td>`; }
function chip(done: boolean): string { return done ? '<span class="chip ok"><span class="d"></span>zugeordnet</span>' : '<span class="chip miss"><span class="d"></span>offen</span>'; }

/**
 * Angezeigte Einheiten-Zuordnung: `value` ist der Wert im Dropdown, `saved` sagt, ob er aus einer
 * gespeicherten Mapping-Zeile stammt oder nur der automatische Vorschlag ist.
 */
interface EffectiveUnit { value: string | null; saved: boolean }

/** Gespeicherte Zeile hat Vorrang (auch „kein Grundpreis" = NULL), sonst der Vorschlag. */
function effectiveUnit(id: number, saved: Map<number, string | null>, suggested: Map<number, string | null>): EffectiveUnit {
  if (saved.has(id)) return { value: saved.get(id) ?? null, saved: true };
  return { value: suggested.get(id) ?? null, saved: false };
}

/**
 * Drei Zustände statt zwei. Der alte `chip(selected != null)` zeigte für einen bloßen VORSCHLAG
 * dasselbe grüne „zugeordnet" wie für eine gespeicherte Zeile — die Ansicht behauptete damit eine
 * Zuordnung, die nie gespeichert wurde (der Zähler oben zählte korrekt nur gespeicherte Zeilen, was
 * genau umgekehrt aussah). Der Import selbst nutzt denselben Vorschlag als Fallback, funktioniert
 * also auch ohne Speichern — die Anzeige darf das nur nicht verwechseln.
 */
function unitChip(sel: EffectiveUnit): string {
  if (sel.saved) return chip(sel.value != null);
  if (sel.value != null) return '<span class="chip sugg" title="Automatischer Vorschlag — noch nicht gespeichert"><span class="d"></span>Vorschlag</span>';
  return chip(false);
}

async function viewKundengruppen(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const data = await loadCustomerGroups(sctx, DEFAULT_SHOP);
  const gambioGroups = data.gambioCustomerGroups ?? [];
  const varioPriceGroups = data.varioPriceGroups ?? [];
  const varioCustomerGroups = data.varioCustomerGroups ?? [];
  // mappings keyed by gambio statusId
  const mappingByGambioId = new Map(
    (data.mappings ?? []).filter((m) => m.gambioId != null).map((m) => [m.gambioId!, m])
  );
  const done = gambioGroups.filter((g) => {
    const m = mappingByGambioId.get(g.statusId);
    return m != null && (m.varioId != null || m.varioCustomerGroupId != null);
  }).length;
  const sub = document.getElementById('go-psub'); if (sub) sub.textContent = `${done}/${gambioGroups.length} zugeordnet`;
  setOpenCount(body.closest('.go-settings') as HTMLElement, 'kundengruppen', gambioGroups.length - done);
  if (gambioGroups.length === 0) {
    body.innerHTML = `<div class="hint">Keine Gambio-Kundengruppen gefunden (oder Liste nicht abrufbar).</div>`;
    return;
  }

  function buildPriceGroupOpts(gambioLabel: string, selectedId: string | null): string {
    const emptyOpt = `<option value="">— keine —</option>`;
    const opts = varioPriceGroups.map((p) =>
      `<option value="${escHtml(p.id)}"${p.id === selectedId ? ' selected' : ''}>${escHtml(p.label)}</option>`
    );
    const newOpt = `<option value="__new__">➕ neu: &quot;${escHtml(gambioLabel)}&quot;</option>`;
    return emptyOpt + opts.join('') + newOpt;
  }

  function buildCustomerGroupOpts(gambioLabel: string, selectedId: string | null): string {
    const emptyOpt = `<option value="">— keine —</option>`;
    const opts = varioCustomerGroups.map((p) =>
      `<option value="${escHtml(p.id)}"${p.id === selectedId ? ' selected' : ''}>${escHtml(p.label)}</option>`
    );
    const newOpt = `<option value="__new__">➕ neu: &quot;${escHtml(gambioLabel)}&quot;</option>`;
    return emptyOpt + opts.join('') + newOpt;
  }

  const rows = gambioGroups.map((g) => {
    const cur = mappingByGambioId.get(g.statusId);
    const hasPriceGroup = cur?.varioId != null;
    const hasCustomerGroup = cur?.varioCustomerGroupId != null;
    const isDone = hasPriceGroup || hasCustomerGroup;
    // Ausgangswerte an der Zeile: der gemeinsame Speichern-Knopf schreibt nur, was sich
    // wirklich geaendert hat.
    return `<tr data-gid="${escHtml(String(g.statusId))}" data-glabel="${escHtml(g.label)}" data-orig-vcg="${escHtml(cur?.varioCustomerGroupId ?? '')}" data-orig-vpg="${escHtml(cur?.varioId ?? '')}">
      <td style="min-width:380px;">
        <div style="display:flex;gap:8px;align-items:flex-end;">
          <div style="flex:1;min-width:0;">
            <div class="inp-label" style="font-size:11px;color:#888;margin-bottom:3px;">Vario-Kundengruppe</div>
            <div class="selwrap"><select class="sel" data-cg="vcg">${buildCustomerGroupOpts(g.label, cur?.varioCustomerGroupId ?? null)}</select></div>
          </div>
          <div style="flex:1;min-width:0;">
            <div class="inp-label" style="font-size:11px;color:#888;margin-bottom:3px;">Vario-Preisgruppe</div>
            <div class="selwrap"><select class="sel" data-cg="vpg">${buildPriceGroupOpts(g.label, cur?.varioId ?? null)}</select></div>
          </div>
        </div>
      </td>
      ${arrowCell()}
      <td class="g-cell">
        <div class="v-cell"><span class="vn">${escHtml(g.label)}</span><span class="vid mono">${escHtml(String(g.statusId))}</span></div>
      </td>
      <td class="st-cell" style="width:auto;white-space:nowrap;"><div style="display:flex;align-items:center;gap:10px;justify-content:flex-end;">${chip(isDone)}${isDone ? `<button class="btn btn-ghost btn-sm" data-cg-clear>Zuordnung entfernen</button>` : ''}</div></td>
    </tr>`;
  }).join('');

  body.innerHTML = `<div class="actions" style="margin-top:0;margin-bottom:14px;"><button class="btn btn-primary" id="go-cg-save">Speichern</button></div>`
    + maptableShell('Vario (Kundengruppe · Preisgruppe)', 'Gambio-Kundengruppe', rows);

  // Zustand des Shop-Schalters „Kundengruppencheck" (GROUP_CHECK). Bewusst NUR auf die Sichtbarkeit
  // am Artikel bezogen: verifiziert ist, dass ohne aktiven Check die Kundengruppen-Freigabe am
  // Artikel ohne Wirkung bleibt. Ob Preisfindung, Kundenexport und Kundenimport ebenfalls davon
  // abhaengen, ist NICHT geprueft und wird hier deshalb nicht behauptet.
  // `null` = nicht ermittelbar → gar keine Anzeige, statt einen falschen Zustand zu zeigen.
  if (data.groupCheckActive === true || data.groupCheckActive === false) {
    // Deeplink auf die Gambio-Konfigurationsseite, Bereich „Kunden" (dort sitzt der Schalter).
    // MUSS ein echter Anchor mit target="_blank" sein — die App laeuft im Vario-iframe ohne
    // allow-popups, `window.open` tut dort sichtbar nichts.
    const cfgUrl = gambioCustomerConfigAdminUrl(data.shopBaseUrl);
    const link = cfgUrl
      ? ` <a href="${escHtml(cfgUrl)}" target="_blank" rel="noopener">In Gambio öffnen ↗</a>`
      : '';
    const gc = document.createElement('div');
    gc.className = 'hint';
    gc.style.cssText = 'margin-top:12px;';
    gc.innerHTML = (data.groupCheckActive
      ? 'Kundengruppencheck im Shop: <strong>aktiv</strong>. Die Kundengruppen-Freigabe am Artikel wirkt damit im Shop.'
      : 'Kundengruppencheck im Shop: <strong>nicht aktiv</strong>. Die Kundengruppen-Freigabe am Artikel bleibt dadurch <strong>ohne Wirkung</strong> — Artikel sind für alle Kundengruppen sichtbar, unabhängig von der Einstellung am Artikel. Der Schalter lässt sich nur im Gambio-Backend aktivieren.'
    ) + link;
    body.appendChild(gc);
  }

  const cgHint = document.createElement('div');
  cgHint.className = 'hint';
  cgHint.style.cssText = 'margin-top:12px;';
  cgHint.innerHTML = 'Diese Zuordnung wirkt auf die <strong>Preisfindung</strong> (beide Felder gesetzt = VARIO verknüpft sie <strong>UND</strong>), auf den <strong>Kundenexport</strong> und auf den <strong>Kundenimport</strong>: Zugeordnet wird, sobald die Vario-Kundengruppe <strong>oder</strong> die Preisgruppe des Kunden hier hinterlegt ist. <strong>Ohne passende Zuordnung wird ein Kunde nicht exportiert</strong>, es gibt keine Standard-Kundengruppe, in die er ersatzweise fällt.';
  body.appendChild(cgHint);

  // Hinweis zum Admin-Schutz des Exports (Guard: `PROTECTED_GAMBIO_STATUS_IDS` im Worker).
  //
  // ACHTUNG beim Ändern: Der Text war zunächst länger und nannte auch die Grenze des Schutzes (eine
  // im Shop neu angelegte Gruppe für interne Konten ist NICHT geschützt). Auf ausdrückliche Vorgabe
  // (User 2026-07-28) bewusst wieder entfernt: für normale Anwender kaum verständlich und im Alltag
  // überflüssig. Die Grenze ist damit ABSICHTLICH nicht mehr im UI dokumentiert, sondern nur am
  // Kommentar von `PROTECTED_GAMBIO_STATUS_IDS` und im Changelog. Bitte nicht „wiederherstellen".
  //
  // Deshalb auch neutrales `.hint` statt des Amber-Warnbanners: der Satz beruhigt, er warnt nicht.
  const cgProtectHint = document.createElement('div');
  cgProtectHint.className = 'hint';
  cgProtectHint.style.cssText = 'margin-top:10px;';
  cgProtectHint.innerHTML = 'Die <strong>Administrator-Gruppe</strong> ist vor dem Überschreiben geschützt. '
    + 'Der Kundenexport fasst Gambios Administrator-Konten nie an.';
  body.appendChild(cgProtectHint);

  /** Loest `__new__` auf (legt die Gruppe in VARIO an) und liefert id + Bezeichnung. */
  const resolveChoice = async (
    kind: 'vcg' | 'vpg', value: string, gambioLabel: string,
  ): Promise<{ id: string | null; label: string | null }> => {
    if (!value) return { id: null, label: null };
    if (value === '__new__') {
      const created = kind === 'vcg'
        ? await createCustomerGroup(sctx, DEFAULT_SHOP, gambioLabel)
        : await createPriceGroup(sctx, DEFAULT_SHOP, gambioLabel);
      if (!created.id) {
        throw new Error(kind === 'vcg'
          ? 'Vario-Kundengruppe konnte nicht angelegt werden.'
          : 'Vario-Preisgruppe konnte nicht angelegt werden.');
      }
      return { id: created.id, label: created.label };
    }
    const list = kind === 'vcg' ? varioCustomerGroups : varioPriceGroups;
    return { id: value, label: list.find((x) => x.id === value)?.label ?? null };
  };

  /** Schreibt eine Zeile. `clear` setzt beide VARIO-Seiten auf null (Zuordnung entfernen). */
  const writeRow = async (tr: HTMLElement, clear = false): Promise<void> => {
    const gambioStatusId = Number(tr.getAttribute('data-gid')!);
    const glabel = tr.getAttribute('data-glabel')!;
    const vcg = clear
      ? { id: null, label: null }
      : await resolveChoice('vcg', tr.querySelector<HTMLSelectElement>('[data-cg="vcg"]')!.value, glabel);
    const vpg = clear
      ? { id: null, label: null }
      : await resolveChoice('vpg', tr.querySelector<HTMLSelectElement>('[data-cg="vpg"]')!.value, glabel);
    await setCustomerGroup(sctx, DEFAULT_SHOP, {
      varioPriceGroupId: vpg.id,
      varioPriceGroupLabel: vpg.label,
      gambioStatusId,
      gambioStatusLabel: glabel,
      varioCustomerGroupId: vcg.id,
      varioCustomerGroupLabel: vcg.label,
    });
  };

  // EIN Speichern-Knopf oben rechts fuer alle Zeilen. Geschrieben wird nur, was vom Ausgangswert
  // abweicht - sonst laufen bei jedem Klick Schreibvorgaenge fuer unveraenderte Zeilen.
  // Wurden an einer zugeordneten Zeile BEIDE Auswahlfelder geleert, ist das die Entfernung der
  // Zuordnung; der Toast benennt das getrennt, damit niemand raten muss, was passiert ist.
  const cgSave = body.querySelector<HTMLButtonElement>('#go-cg-save')!;
  cgSave.onclick = () => {
    const changed = Array.from(body.querySelectorAll<HTMLTableRowElement>('tr[data-gid]')).filter((tr) => {
      const vcg = tr.querySelector<HTMLSelectElement>('[data-cg="vcg"]')!.value;
      const vpg = tr.querySelector<HTMLSelectElement>('[data-cg="vpg"]')!.value;
      return vcg !== (tr.getAttribute('data-orig-vcg') || '') || vpg !== (tr.getAttribute('data-orig-vpg') || '');
    });
    if (changed.length === 0) { showToast('Keine Änderungen zu speichern.', 'info'); return; }

    const cleared = changed.filter((tr) =>
      !tr.querySelector<HTMLSelectElement>('[data-cg="vcg"]')!.value
      && !tr.querySelector<HTMLSelectElement>('[data-cg="vpg"]')!.value);

    cgSave.disabled = true;
    void (async () => {
      try {
        for (const tr of changed) await writeRow(tr);
        const kept = changed.length - cleared.length;
        const parts: string[] = [];
        if (kept > 0) parts.push(`${kept} Zuordnung${kept === 1 ? '' : 'en'} gespeichert`);
        if (cleared.length > 0) parts.push(`${cleared.length} entfernt`);
        showToast(parts.join(', '), 'success');
        void rerender(body.closest('.go-settings') as HTMLElement);
      } catch (e) {
        zeigeFehler('Aktion fehlgeschlagen', e);
        cgSave.disabled = false;
      }
    })();
  };

  // Je Zeile: nur die Zuordnung loesen. Die Gruppen selbst bleiben in beiden Systemen unberuehrt.
  body.querySelectorAll<HTMLButtonElement>('[data-cg-clear]').forEach((btn) => {
    btn.onclick = () => {
      const tr = btn.closest('tr')! as HTMLElement;
      btn.disabled = true;
      void writeRow(tr, true)
        .then(() => {
          showToast('Zuordnung entfernt', 'success');
          void rerender(body.closest('.go-settings') as HTMLElement);
        })
        .catch((e: Error) => { zeigeFehler('Aktion fehlgeschlagen', e); btn.disabled = false; });
    };
  });
}

async function viewApi(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const [shop, channels, pubCreds] = await Promise.all([
    defaultShop(sctx),
    loadChannels(sctx).catch(() => [] as ChannelOption[]),
    loadShopPublicCreds(sctx, DEFAULT_SHOP).catch(() => null),
  ]);
  const sub = document.getElementById('go-psub'); if (sub) sub.textContent = 'Zugangsdaten & Vario-Kanal dieses Shops';
  if (!shop) { body.innerHTML = `<div class="hint">Noch kein Gambio-Shop verbunden. Bitte zuerst über „Gambio installieren" verbinden.</div>`; return; }
  const assignedId = shop.vario_channel?.id ?? '';
  const opts = ['<option value="">— Kanal wählen —</option>', ...channels.map((c) => {
    const used = c.assignedToShopKey && c.assignedToShopKey !== shop.shop_key ? ` (belegt: ${escHtml(c.assignedToLabel ?? '')})` : '';
    return `<option value="${escHtml(c.id)}"${c.id === assignedId ? ' selected' : ''}>${escHtml(c.label)}${used}</option>`;
  })].join('');
  body.innerHTML = `
    <div class="actions" style="margin-top:0;margin-bottom:14px;"><button class="btn btn-primary" id="go-cred-save">Speichern &amp; prüfen</button></div>
    <div class="sec-lab">Zugangsdaten</div>
    <div class="frow">
      <div class="field full"><label>Shop-URL (Basis-Adresse)</label><input class="inp mono" id="go-cred-url" value="${escHtml(pubCreds?.base_url ?? shop.base_url ?? '')}"><span class="hint">Verbindungsstatus: ${escHtml(shop.status)}</span></div>
    </div>
    <div class="frow">
      <div class="field"><label>Benutzername</label><input class="inp" id="go-cred-user" value="${escHtml(pubCreds?.username ?? '')}"></div>
      <div class="field"><label>Passwort</label><input class="inp" type="password" id="go-cred-pw" placeholder="leer lassen = unverändert" autocomplete="new-password"></div>
    </div>
    <div class="sec-lab">Vario-Verkaufskanal</div>
    <div class="frow"><div class="field full"><label>Zugewiesener Kanal</label>
      <div class="lager-row"><div class="selwrap"><select class="sel" id="go-chan">${opts}</select></div>
      <button class="btn btn-primary" id="go-chan-assign">Zuweisen</button>
      <button class="btn btn-ghost" id="go-chan-create">Kanal anlegen</button></div></div></div>`;

  body.querySelector<HTMLButtonElement>('#go-cred-save')!.onclick = () => {
    const urlVal = (body.querySelector<HTMLInputElement>('#go-cred-url')!.value ?? '').trim();
    const userVal = (body.querySelector<HTMLInputElement>('#go-cred-user')!.value ?? '').trim();
    const pwVal = (body.querySelector<HTMLInputElement>('#go-cred-pw')!.value ?? '');
    if (!pwVal) { showToast('Passwort zum Ändern erforderlich.', 'error'); return; }
    const btn = body.querySelector<HTMLButtonElement>('#go-cred-save')!;
    btn.disabled = true;
    void saveCredentials(sctx, urlVal, userVal, pwVal).then((r) => {
      if (r.ok) {
        showToast('Zugangsdaten gespeichert und geprüft.', 'success');
        // Verbindungs-Pille neu messen: nach neuen Zugangsdaten muss aus rot sofort grün werden.
        void refreshConnectionPills();
        void rerender(body.closest('.go-settings') as HTMLElement);
      } else {
        showToast(r.error ?? 'Speichern fehlgeschlagen.', 'error');
        btn.disabled = false;
      }
    });
  };

  body.querySelector<HTMLButtonElement>('#go-chan-assign')!.onclick = () => {
    const id = body.querySelector<HTMLSelectElement>('#go-chan')!.value;
    if (!id) { showToast('Bitte zuerst einen Kanal wählen.', 'error'); return; }
    void assignChannel(sctx, shop.shop_key, id).then((r) => showToast(r.ok ? `Kanal „${r.label}" zugewiesen.` : (r.error ?? 'Fehler'), r.ok ? 'success' : 'error'));
  };

  body.querySelector<HTMLButtonElement>('#go-chan-create')!.onclick = () => {
    const btn = body.querySelector<HTMLButtonElement>('#go-chan-create')!;
    btn.disabled = true;
    showToast('Vario-Verkaufskanal wird angelegt …', 'info');
    void createChannel(sctx, shop.shop_key).then((r) => {
      if (r.ok) {
        showToast(r.alreadyPresent ? `Kanal „${r.label}" bereits vorhanden.` : `Verkaufskanal „${r.label}" angelegt.`, 'success');
        void rerender(body.closest('.go-settings') as HTMLElement);
      } else {
        showToast(r.error ?? 'Kanal anlegen fehlgeschlagen.', 'error');
        btn.disabled = false;
      }
    });
  };
}

async function viewLager(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const [cur, whs] = await Promise.all([loadShopSettings(sctx, DEFAULT_SHOP), loadWarehouses(sctx)]);
  const c = cur ?? {};
  const sub = document.getElementById('go-psub'); if (sub) sub.textContent = 'Lager, dessen Bestand an Gambio gemeldet wird';
  const whOpts = ['<option value="">— bitte auswählen —</option>', ...whs.map((w) => {
    const id = String(w.id); const label = w.displayName ?? w.number ?? id;
    return `<option value="${escHtml(id)}"${id === String(c['vario_storage_id'] ?? '') ? ' selected' : ''}>${escHtml(label)}</option>`;
  })].join('');
  body.innerHTML = `
    <div class="frow"><div class="field full"><label>Standard-Lager</label><div class="selwrap"><select class="sel" id="go-wh">${whOpts}</select></div></div></div>
    <div class="toggle-card"><div class="tc-body"><div class="tc-t">Bestand NICHT zu Gambio pushen</div><div class="tc-s">Nur lesen - keine Bestandsübertragung an den Shop.</div></div><button class="sw ${c['suppress_stock_push'] ? 'on' : ''}" id="go-suppress"></button></div>
    <div class="frow" style="margin-top:14px;"><div class="field"><label>Virtueller Lagerbestand (bestandslose Artikel)</label><input class="inp" type="number" min="0" step="1" id="go-vstock" value="${escHtml(String(c['virtual_stock'] ?? 9999))}"></div></div>

    <div class="sec-lab" style="margin-top:22px;">Bestand aus dem Shop übernehmen</div>
    <div class="hint">Bestände kommen <strong>ausschließlich über eine Inventur</strong> nach VARIO, nie per Direktbuchung. Der Knopf legt in VARIO eine Inventur an und trägt die Gambio-Bestände als Zählliste ein — auch Artikel mit Menge 0, denn „gezählt, keine da" ist eine Aussage. <strong>Eingebucht wird nichts:</strong> Prüfen, Einkaufspreise nachtragen und Abschließen machst Du in VARIO unter <em>Lager → Inventuren</em>.</div>
    <div class="frow" style="margin-top:10px;"><div class="field full">
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
        <button class="btn" id="go-inv-initial">Anfangsbestand anlegen</button>
        <button class="btn" id="go-inv-stichtag">Zählliste anlegen</button>
        ${varioInventoriesUrl() ? `<a class="btn btn-ghost btn-sm" href="${escHtml(varioInventoriesUrl()!)}" target="_blank" rel="noopener">Inventuren in VARIO öffnen</a>` : ''}
      </div>
    </div></div>
    <div class="hint"><strong>Achtung:</strong> Eine eröffnete Inventur <strong>sperrt das Lager für Buchungen</strong> — auch für den Bestands-Push an Gambio und für VARIO selbst. Die Sperre bleibt bestehen, bis die Inventur in VARIO <em>beendet</em> ist.</div>
`;
  const supp = body.querySelector<HTMLButtonElement>('#go-suppress')!;
  const whSel = body.querySelector<HTMLSelectElement>('#go-wh')!;
  const vstock = body.querySelector<HTMLInputElement>('#go-vstock')!;
  /** Letzter GESPEICHERTER virtueller Bestand — Rueckfall, wenn eine Eingabe unbrauchbar ist. */
  let lastSavedVstock = String(c['virtual_stock'] ?? 9999);

  const saveAll = (): Promise<void> => saveLager(sctx, DEFAULT_SHOP, c, {
    storageId: whSel.value || null,
    storageLabel: whSel.value ? (whSel.options[whSel.selectedIndex]?.text ?? null) : null,
    suppressStock: supp.classList.contains('on'),
    virtualStock: Number(vstock.value),
  });

  autoSaveSwitch(supp, saveAll);

  // --- Inventur: Anfangsbestand oder Stichtags-Zählliste ---------------------
  // Bewusst ein MODAL statt eines zweistufigen Klicks: hier entsteht ein Objekt in VARIO, das
  // anschliessend das Lager sperrt. Das muss man gelesen haben, bevor man es ausloest — ein
  // zweiter Klick auf denselben Knopf liest sich niemand durch. `window.confirm` ist verboten.
  const starteInventur = (art: 'INITIAL' | 'CLOSING_DATE'): void => {
    const istAnfang = art === 'INITIAL';
    const titel = istAnfang ? 'Anfangsbestand als Inventur anlegen' : 'Zählliste anlegen';
    const modal = openModal({
      title: titel,
      icon: 'fa-clipboard-list',
      width: 560,
      body: `
        <div class="kl-modal-meta">
          <strong>${istAnfang ? 'Anfangsbestand' : 'Stichtags-Zählliste'}</strong>
          <div class="text-grey">Lager: ${escHtml(String(c['vario_storage_label'] ?? '— keins gewählt —'))} · Stichtag: heute</div>
        </div>
        <div class="kl-form-hint">
          In VARIO entsteht eine Inventur mit den Gambio-Beständen als Zählliste. <strong>Eingebucht
          wird nichts</strong> — das machst Du danach selbst unter <em>Lager → Inventuren</em>${
            varioInventoriesUrl()
              ? `, <a href="${escHtml(varioInventoriesUrl()!)}" target="_blank" rel="noopener">hier direkt zu öffnen</a>`
              : ''}.
        </div>
        <div class="kl-form-hint">
          <strong>In der Zählliste stehen nur Artikel aus dem Shop.</strong> VARIO erzeugt die Liste
          zunächst für das ganze Lager; alle Zeilen ohne Shop-Bezug werden wieder entfernt.
          <strong>Diese Artikel musst Du anderweitig zählen</strong> — sie stünden sonst mit Menge 0
          in der Liste, und beim Einbuchen würde ihr Bestand genullt.
        </div>
        <div class="kl-modal-error" style="display:block;">
          <strong>Eine eröffnete Inventur sperrt das Lager für Buchungen.</strong> Solange sie offen
          ist, kann weder der Bestands-Push an Gambio noch VARIO selbst auf dieses Lager buchen.
          Die Sperre fällt erst, wenn Du die Inventur in VARIO <em>beendest</em>.
        </div>
      `,
      footer: `
        <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
        <button type="button" class="v-button v-button-primary" data-action="go">
          <span class="v-icon fa-regular fa-check"></span> ${escHtml(titel)}
        </button>
      `,
    });
    modal.el.querySelector<HTMLButtonElement>('[data-action="go"]')!.addEventListener('click', () => {
      const btn = modal.el.querySelector<HTMLButtonElement>('[data-action="go"]')!;
      btn.disabled = true;
      btn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lege an …';
      void (async () => {
        try {
          const jobId = await enqueueStockInventory(sctx, DEFAULT_SHOP, art);
          const job = await awaitJob(sctx.supabase, jobId, { timeoutMs: 900_000, pollIntervalMs: 2_000 });
          if (job.status !== 'completed') throw new Error(job.error ?? 'Die Inventur konnte nicht angelegt werden.');
          const rr = (job.result ?? {}) as {
            inventoryNumber?: string | null; gezaehlt?: number; gesamt?: number;
            fehler?: number; negativeAufNullGesetzt?: number;
            verworfen?: number; nichtVerworfen?: number;
          };
          const teile = [`Inventur${rr.inventoryNumber ? ` ${rr.inventoryNumber}` : ''} angelegt`,
            `${rr.gezaehlt ?? 0} von ${rr.gesamt ?? 0} Artikeln gezählt`];
          if (rr.verworfen) teile.push(`${rr.verworfen} Zeilen ohne Shop-Bezug entfernt`);
          if (rr.fehler) teile.push(`${rr.fehler} fehlgeschlagen`);
          if (rr.negativeAufNullGesetzt) teile.push(`${rr.negativeAufNullGesetzt} negative Bestände als 0 gezählt`);
          modal.close();
          // Stehengebliebene Fremdzeilen sind kein Schoenheitsfehler: sie stehen auf Menge 0 und
          // wuerden beim Einbuchen den Bestand dieser Artikel nullen. Das muss der Anwender lesen.
          if (rr.nichtVerworfen) {
            showToast(`${teile.join(' · ')}. ACHTUNG: ${rr.nichtVerworfen} Zeilen ohne Shop-Bezug `
              + 'liessen sich nicht entfernen und stehen auf Menge 0 — vor dem Einbuchen in VARIO '
              + 'prüfen, sonst wird deren Bestand genullt.', 'error');
          } else {
            showToast(`${teile.join(' · ')}. Weiter geht es in VARIO unter Lager → Inventuren.`,
              rr.fehler ? 'error' : 'success');
          }
        } catch (e) {
          modal.close();
          zeigeFehler(titel + ' fehlgeschlagen', e, { aktion: 'gambio_enqueue_stock_inventory', art });
        }
      })();
    });
  };
  body.querySelector<HTMLButtonElement>('#go-inv-initial')!.onclick = () => starteInventur('INITIAL');
  body.querySelector<HTMLButtonElement>('#go-inv-stichtag')!.onclick = () => starteInventur('CLOSING_DATE');

  // Dropdown: speichert beim Auswaehlen, wie in den Zuordnungs-Abschnitten.
  whSel.onchange = () => {
    whSel.disabled = true;
    void saveAll()
      .then(() => showToast('Gespeichert', 'success'))
      .catch((e: Error) => zeigeFehler('Speichern fehlgeschlagen', e))
      .finally(() => { whSel.disabled = false; });
  };

  // Zahlenfeld: `change` feuert beim Verlassen des Feldes bzw. mit Enter, NICHT bei jedem
  // Tastendruck — sonst gaebe es einen Speichervorgang je Ziffer.
  // Eine leere oder unsinnige Eingabe wird NICHT gespeichert: der alte Umgang („leer heisst 9999")
  // war beim Klick auf Speichern noch vertretbar, beim automatischen Speichern wuerde ein
  // versehentliches Leeren des Feldes sofort 9999 festschreiben. Stattdessen Hinweis und der
  // letzte gespeicherte Wert kommt zurueck.
  vstock.onchange = () => {
    const raw = vstock.value.trim();
    const n = Number(raw);
    if (!raw || !Number.isInteger(n) || n < 0) {
      vstock.value = lastSavedVstock;
      showToast('Bitte eine ganze Zahl ab 0 eintragen.', 'error');
      return;
    }
    vstock.disabled = true;
    void saveAll()
      .then(() => { lastSavedVstock = raw; showToast('Gespeichert', 'success'); })
      .catch((e: Error) => zeigeFehler('Speichern fehlgeschlagen', e))
      .finally(() => { vstock.disabled = false; });
  };
}

/** Liest die aktuell gespeicherten GPSR-Werte aus den Shop-Settings. GPSR wird jetzt in der
 *  Sektion „Hersteller-GPSR" gepflegt; andere Speicher-Pfade (saveImport) reichen diese Werte
 *  nur unveraendert durch, damit sie nicht ueberschrieben/geleert werden. */
function currentGpsrSettings(cur: Record<string, unknown>): { mode: string; map: Record<string, Record<string, string>> } {
  return {
    mode: String(cur['gpsr_description_mode'] ?? 'off'),
    map: (cur['gpsr_contact_map'] && typeof cur['gpsr_contact_map'] === 'object'
      ? cur['gpsr_contact_map'] : {}) as Record<string, Record<string, string>>,
  };
}

/**
 * Schalter, der sich SELBST speichert — kein Speichern-Knopf im Abschnitt.
 *
 * Vorgabe des Betreibers 2026-08-24: In Abschnitten, die nur aus Schaltern bestehen, ist ein
 * Speichern-Knopf überflüssig. Dieselbe Bedienung nutzen die Zuordnungs-Abschnitte längst
 * („Auto-Save bei Auswahl", z. B. Paketdienste und Steuerklassen).
 *
 * Drei Dinge, die dabei leicht schiefgehen:
 *  - **Stiller Rückfall anderer Felder.** `saveShopSettings` schreibt IMMER alle Felder aus
 *    `{...cur, ...patch}`. Ein Patch mit nur EINEM Schalter würde den zweiten Schalter des
 *    Abschnitts auf den Stand des Seitenaufrufs zurückdrehen. Deshalb schickt der Aufrufer hier
 *    stets den Zustand ALLER Schalter seines Abschnitts mit, nicht nur den gerade geklickten.
 *  - **Die Oberfläche darf nichts behaupten.** Scheitert das Speichern, wird der Schalter
 *    zurückgedreht; sonst zeigt er einen Zustand, den die Datenbank nicht kennt.
 *  - **Doppelklicks.** Während des Speicherns ist der Schalter gesperrt, damit sich zwei Läufe
 *    nicht überholen.
 */
function autoSaveSwitch(el: HTMLButtonElement, save: () => Promise<void>): void {
  el.onclick = () => {
    const wasOn = el.classList.contains('on');
    el.classList.toggle('on', !wasOn);
    el.disabled = true;
    void save()
      .then(() => showToast('Gespeichert', 'success'))
      .catch((e: Error) => {
        el.classList.toggle('on', wasOn);
        zeigeFehler('Speichern fehlgeschlagen', e);
      })
      .finally(() => { el.disabled = false; });
  };
}

async function viewImport(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const cur = (await loadShopSettings(sctx, DEFAULT_SHOP)) ?? {};
  const sub = document.getElementById('go-psub'); if (sub) sub.textContent = 'Wie Gambio-Daten beim Import nach Vario übernommen werden';
  const writeMaster = cur['import_write_master'] === true;
  const overwriteMaster = cur['import_overwrite_master'] === true;
  const additionalToVariants = cur['import_additional_to_variants'] !== false; // Default an
  const writeDefaultChannelPrice = cur['import_write_default_channel_price'] === true; // Default aus
  body.innerHTML = `
    <div class="toggle-card"><div class="tc-body"><div class="tc-t">Bezeichnung + Beschreibung in die Vario-Stammdaten schreiben</div><div class="tc-s">Beim Import werden Name und Beschreibung auch am VARIO-Artikel selbst gesetzt, nicht nur am Kanal-Listing.</div></div><button class="sw ${writeMaster ? 'on' : ''}" id="go-imp-write"></button></div>
    <div class="toggle-card"><div class="tc-body"><div class="tc-t">Vorhandene Vario-Stammdaten überschreiben</div><div class="tc-s">Bereits gepflegte Vario-Stammdaten werden beim Import überschrieben.</div></div><button class="sw ${overwriteMaster ? 'on' : ''}" id="go-imp-overwrite"></button></div>
    <div class="toggle-card"><div class="tc-body"><div class="tc-t">Zusatzfelder des Variantenhauptartikels auf die Varianten übertragen</div><div class="tc-s">Nötig, wenn Varianten flach auf andere Kanäle ausgesteuert werden.</div></div><button class="sw ${additionalToVariants ? 'on' : ''}" id="go-imp-additional"></button></div>
    <div class="hint">Der Preis aus dem Shop wird beim Import immer auf den Gambio-Verkaufskanal geschrieben.</div>
    <div class="toggle-card"><div class="tc-body"><div class="tc-t">Preis zusätzlich als Vario-Grundpreis schreiben</div><div class="tc-s">Der Preis landet dann auch auf dem Standard-Verkaufskanal und erscheint am Artikel als Verkaufspreis. Andere Verkaufskanäle ohne eigenen Preis übernehmen ihn damit ebenfalls. Aktionspreise bleiben immer nur am Gambio-Verkaufskanal.</div></div><button class="sw ${writeDefaultChannelPrice ? 'on' : ''}" id="go-imp-defaultprice"></button></div>
`;
  const swWrite = body.querySelector<HTMLButtonElement>('#go-imp-write')!;
  const swOverwrite = body.querySelector<HTMLButtonElement>('#go-imp-overwrite')!;
  const swAdditional = body.querySelector<HTMLButtonElement>('#go-imp-additional')!;
  const swDefaultPrice = body.querySelector<HTMLButtonElement>('#go-imp-defaultprice')!;
  // Jeder Klick speichert sofort und schickt den Zustand ALLER Schalter mit (siehe
  // autoSaveSwitch: ein Patch mit nur einem Feld wuerde die anderen zurueckdrehen).
  const saveAll = (): Promise<void> => {
    // GPSR-Bestandswerte unveraendert durchreichen (Pflege erfolgt in „Hersteller-GPSR").
    const gpsr = currentGpsrSettings(cur);
    return saveImport(sctx, DEFAULT_SHOP, cur, {
      importWriteMaster: swWrite.classList.contains('on'),
      importOverwriteMaster: swOverwrite.classList.contains('on'),
      importAdditionalToVariants: swAdditional.classList.contains('on'),
      importWriteDefaultChannelPrice: swDefaultPrice.classList.contains('on'),
      gpsrDescriptionMode: gpsr.mode,
      gpsrContactMap: gpsr.map,
    });
  };
  for (const el of [swWrite, swOverwrite, swAdditional, swDefaultPrice]) autoSaveSwitch(el, saveAll);
}

async function viewHerstellerGpsr(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const sub = document.getElementById('go-psub');
  if (sub) sub.textContent = 'GPSR-Herstellerangaben (Hersteller & EU-Bevollmächtigter), die beim Anlegen/Aktualisieren der Artikel in Gambio gesetzt werden.';
  const [cur, contactData] = await Promise.all([
    loadShopSettings(sctx, DEFAULT_SHOP).then((r) => r ?? {}),
    loadContactTypeOptions(sctx, DEFAULT_SHOP).catch(() => ({ items: [] as { id: string; label: string }[] })),
  ]);
  const contactOpts = contactData.items;
  // Vario-Host aus dem iframe-Embedder, nicht aus der Edge-Function-Antwort: EIN Weg fuer alle
  // Vario-Deeplinks, und ohne Roundtrip. Siehe lib/vario-links.ts.
  const contactTypesUrl = varioContactTypesUrl();
  const gpsr = String(cur['gpsr_description_mode'] ?? 'off');
  const gpsrContactMap = (cur['gpsr_contact_map'] && typeof cur['gpsr_contact_map'] === 'object'
    ? cur['gpsr_contact_map'] : {}) as Record<string, Record<string, string> | undefined>;
  const gpsrRoleField = (set: string, role: string, label: string, full = false): string => {
    const v = (gpsrContactMap[set]?.[role] ?? '');
    const known = contactOpts.some((o) => o.label.toLowerCase() === v.toLowerCase());
    const opts = [
      `<option value="">—</option>`,
      ...contactOpts.map((o) => `<option value="${escHtml(o.label)}"${o.label === v ? ' selected' : ''}>${escHtml(o.label)}</option>`),
      (v && !known ? `<option value="${escHtml(v)}" selected>${escHtml(v)} (eigenes)</option>` : ''),
      `<option value="__custom__">eigenes Label…</option>`,
    ].join('');
    return `<div class="field${full ? ' full' : ''}"><label>${label}</label>`
      + `<div class="selwrap"><select class="sel" data-gpsr-sel id="go-gpsr-${set}-${role}">${opts}</select></div>`
      + `<input class="inp" type="text" id="go-gpsr-${set}-${role}-custom" placeholder="eigenes Vario-Kontakt-Label" value="${(v && !known) ? escHtml(v) : ''}" style="${(v && !known) ? '' : 'display:none;'}margin-top:4px;"></div>`;
  };
  const gpsrSetBlock = (set: string, title: string): string => `
    <div class="sec-lab" style="margin-top:14px;">${title}</div>
    <div class="frow">
      ${gpsrRoleField(set, 'phone', 'Telefon')}
      ${gpsrRoleField(set, 'email', 'E-Mail')}
      ${gpsrRoleField(set, 'contactForm', 'Kontaktformular', true)}
    </div>`;
  body.innerHTML = `
    <div class="actions" style="display:flex;justify-content:flex-end;margin-bottom:14px;"><button class="btn btn-primary" id="go-imp-save">Speichern</button></div>
    <div class="frow"><div class="field full"><label>GPSR-Herstellerangaben</label><div class="selwrap"><select class="sel" id="go-imp-gpsr">
      <option value="off"${gpsr === 'off' ? ' selected' : ''}>Aus</option>
      <option value="append"${gpsr === 'append' ? ' selected' : ''}>An Beschreibung anhängen</option>
      <option value="tab"${gpsr === 'tab' ? ' selected' : ''}>Eigener Tab ([TAB:Herstellerangaben])</option>
    </select></div></div></div>
    <div id="go-gpsr-map" style="display:${gpsr === 'off' ? 'none' : 'block'};">
      <div class="hint">Vario-Kontakt-Label je Rolle - wird beim Aufbau der GPSR-Herstellerangaben für Telefon, E-Mail und Kontaktformular aufgelöst.</div>
      ${gpsrSetBlock('manufacturer', 'Hersteller')}
      ${gpsrSetBlock('euRep', 'EU-Bevollmächtigter')}
      <div class="hint" style="margin-top:12px;">
        Neue Kontakttypen legst du direkt in Vario an - sie erscheinen dann hier in der Auswahl.${contactTypesUrl
          ? ` <a href="${escHtml(contactTypesUrl)}" target="_blank" rel="noopener">Kontakttypen in Vario verwalten <span class="v-icon fa-regular fa-arrow-up-right-from-square" style="font-size:.8em;"></span></a>`
          : ''}
      </div>
    </div>`;
  const gpsrSel = body.querySelector<HTMLSelectElement>('#go-imp-gpsr')!;
  const gpsrMapBox = body.querySelector<HTMLElement>('#go-gpsr-map')!;
  gpsrSel.addEventListener('change', () => {
    gpsrMapBox.style.display = gpsrSel.value === 'off' ? 'none' : 'block';
  });
  // Freitext-Fallback: Select „eigenes Label…" blendet das zugehörige -custom-Input ein.
  body.querySelectorAll<HTMLSelectElement>('[data-gpsr-sel]').forEach((sel) => {
    const customInp = body.querySelector<HTMLInputElement>(`#${sel.id}-custom`);
    if (!customInp) return;
    sel.addEventListener('change', () => {
      customInp.style.display = sel.value === '__custom__' ? 'block' : 'none';
    });
  });
  const buildContactMap = (): Record<string, Record<string, string>> => {
    const out: Record<string, Record<string, string>> = {};
    for (const set of ['manufacturer', 'euRep']) {
      const roleObj: Record<string, string> = {};
      for (const role of ['phone', 'email', 'contactForm']) {
        const sel = body.querySelector<HTMLSelectElement>(`#go-gpsr-${set}-${role}`);
        if (!sel) continue;
        let v: string;
        if (sel.value === '__custom__') {
          v = (body.querySelector<HTMLInputElement>(`#go-gpsr-${set}-${role}-custom`)?.value ?? '').trim();
        } else {
          v = sel.value.trim();
        }
        if (v) roleObj[role] = v;
      }
      if (Object.keys(roleObj).length > 0) out[set] = roleObj;
    }
    return out;
  };
  body.querySelector<HTMLButtonElement>('#go-imp-save')!.onclick = () => {
    void saveGpsr(sctx, DEFAULT_SHOP, cur, {
      gpsrDescriptionMode: gpsrSel.value,
      gpsrContactMap: buildContactMap(),
    }).then(() => showToast('Einstellungen gespeichert.', 'success')).catch((e: Error) => zeigeFehler('Speichern fehlgeschlagen', e));
  };
}

/**
 * „Anlage-Prüfung": welche Angaben ein Vario-Artikel haben muss, damit er im Reiter
 * „Auf Gambio anlegen" als bereit gilt.
 *
 * Die vier HARTEN Regeln stehen hier bewusst nur als Text und ohne Schalter: ohne sie ist das
 * Produkt im Shop kaputt (keine Kategorie -> über die Navigation nicht erreichbar, kein Preis ->
 * 0,00 €, keine Bezeichnung -> Artikelnummer als Name, kein gemapptes Steuerschema ->
 * Standardsatz). Wählbar sind nur die vier weichen.
 */
async function viewAnlagepruefung(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const cur = (await loadShopSettings(sctx, DEFAULT_SHOP)) ?? {};
  const sub = document.getElementById('go-psub');
  if (sub) sub.textContent = 'Welche Angaben ein Vario-Artikel haben muss, bevor er als bereit für die Anlage gilt';

  const roh = cur['readiness_optional_checks'];
  // Kein Wert = Auslieferungszustand (alles außer VPE). Ein LEERES Array ist dagegen eine bewusste
  // Auswahl und darf nicht zu „alle" werden.
  const gewaehlt = new Set<string>(
    Array.isArray(roh) ? (roh as unknown[]).map(String) : ['description', 'manufacturer', 'weight'],
  );

  const weich: Array<{ key: string; titel: string; text: string }> = [
    { key: 'description', titel: 'Beschreibung', text: 'Ohne Beschreibung erscheint die Artikelseite im Shop leer.' },
    { key: 'manufacturer', titel: 'Hersteller', text: 'Ohne Hersteller fehlen im Shop die Herstellerangaben.' },
    { key: 'vpe', titel: 'VPE (Grundpreis)', text: 'Ohne Grundpreis-Angabe kann der Shop keinen Grundpreis ausweisen. Nur für grundpreispflichtige Sortimente (Lebensmittel, Kosmetik, Reinigungsmittel …) sinnvoll — Vario führt dafür kein Kennzeichen, deshalb gilt der Schalter für alle Artikel. Im Auslieferungszustand aus.' },
    { key: 'weight', titel: 'Gewicht', text: 'Ohne Gewicht rechnet der Shop mit 0 kg und ermittelt falsche Versandkosten. Wird nur bei Artikelarten geprüft, die versandt werden — bei Artikeln ohne Bestand, Leistungen und Versandkosten-Artikeln nicht.' },
  ];

  body.innerHTML = `
    <div class="hint">Immer geprüft und nicht abwählbar: <strong>Kategorie</strong>, <strong>Bezeichnung</strong>, <strong>Preis</strong> und <strong>Steuerschema</strong>. Fehlt eine dieser Angaben, ist das Produkt im Shop nicht brauchbar.</div>
    ${weich.map((w) => `<div class="toggle-card"><div class="tc-body"><div class="tc-t">${escHtml(w.titel)}</div><div class="tc-s">${escHtml(w.text)}</div></div><button class="sw ${gewaehlt.has(w.key) ? 'on' : ''}" data-check="${escHtml(w.key)}"></button></div>`).join('')}
    <div class="hint">Die Prüfung warnt nur. Anlegen lässt sich ein Artikel auch dann, wenn Angaben fehlen. Das Ergebnis erscheint im Reiter „Auf Gambio anlegen" in der Spalte „Bereit" und wird bei jedem Klick auf „Vario-Artikel aktualisieren" neu ermittelt.</div>
`;

  const schalter = [...body.querySelectorAll<HTMLButtonElement>('button[data-check]')];
  // Wie in viewImport: jeder Klick schickt den Zustand ALLER Schalter mit, sonst drehte der Patch
  // die uebrigen auf den Stand des Seitenaufrufs zurueck.
  const saveAll = (): Promise<void> => saveAnlagepruefung(sctx, DEFAULT_SHOP, cur, {
    optionaleChecks: schalter.filter((el) => el.classList.contains('on')).map((el) => el.getAttribute('data-check') ?? ''),
  });
  for (const el of schalter) autoSaveSwitch(el, saveAll);
}

async function viewDarstellung(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const cur = (await loadShopSettings(sctx, DEFAULT_SHOP)) ?? {};
  const sub = document.getElementById('go-psub'); if (sub) sub.textContent = 'Standardwerte, die bei Neu-Listung eines Produkts in Gambio gesetzt werden';
  const d = (cur['display_defaults'] && typeof cur['display_defaults'] === 'object'
    ? cur['display_defaults'] : {}) as Record<string, unknown>;
  const on = (k: string): boolean => d[k] === true;
  const str = (k: string, dflt: string): string => (d[k] === null || d[k] === undefined ? dflt : String(d[k]));
  const enabled = d['enabled'] === true;
  const prio = str('sitemapPriority', '0.5');
  const freq = str('sitemapChangeFrequency', 'daily');
  const priceStatus = str('priceStatus', '0');
  const prioOpts = ['0.0', '0.1', '0.2', '0.3', '0.4', '0.5', '0.6', '0.7', '0.8', '0.9', '1.0'];
  const freqOpts: Array<[string, string]> = [
    ['always', 'immer'], ['hourly', 'stündlich'], ['daily', 'täglich'], ['weekly', 'wöchentlich'],
    ['monthly', 'monatlich'], ['yearly', 'jährlich'], ['never', 'nie'],
  ];
  const priceOpts: Array<[string, string]> = [['0', 'normal kaufbar'], ['1', 'Preis auf Anfrage'], ['2', 'nicht kaufbar']];
  const sid = String(cur['default_shipping_time_id'] ?? 1);
  const shipCustom = !['1', '2', '3'].includes(sid)
    ? `<option value="${escHtml(sid)}" selected>ID ${escHtml(sid)} (eigene)</option>`
    : '';
  const toggleCard = (id: string, title: string, sub2: string, isOn: boolean): string =>
    `<div class="toggle-card"><div class="tc-body"><div class="tc-t">${escHtml(title)}</div><div class="tc-s">${escHtml(sub2)}</div></div><button class="sw ${isOn ? 'on' : ''}" id="${id}"></button></div>`;
  body.innerHTML = `
    ${toggleCard('go-dd-active', 'Aktiv-Status bei Neuzuweisung', 'Gilt IMMER (unabhängig von den Standardwerten unten): neu zugewiesene Artikel werden in Gambio aktiv (an) bzw. inaktiv (aus) angelegt.', cur['default_active'] !== false)}
    ${toggleCard('go-dd-deactivate', 'Produkt bei Entfernung auf inaktiv setzen', 'Wird ein Artikel in Vario aus dem Verkaufskanal genommen oder die Zuordnung aufgelöst, wird das Gambio-Produkt automatisch auf inaktiv gesetzt.', cur['deactivate_on_unassign'] !== false)}
    <div class="frow"><div class="field full"><label>Standard-Lieferstatus (Gambio)</label><div class="selwrap"><select class="sel" id="go-dd-ship">
      <option value="1"${sid === '1' ? ' selected' : ''}>1 - ca. 3-4 Tage</option>
      <option value="2"${sid === '2' ? ' selected' : ''}>2 - ca. 1 Woche</option>
      <option value="3"${sid === '3' ? ' selected' : ''}>3 - ca. 2 Wochen</option>
      ${shipCustom}
    </select></div><span class="hint">Gilt immer: Lieferstatus, den neu in Gambio angelegte Produkte bekommen — unabhängig vom Schalter darunter.</span></div></div>
    ${toggleCard('go-dd-enabled', 'Standardwerte bei Neu-Listung anwenden', 'Nur wenn aktiv, werden die folgenden Werte auf neu übertragene Produkte gesetzt.', enabled)}
      <div class="subgroup${enabled ? '' : ' is-muted'}" id="go-dd-group">
      ${toggleCard('go-dd-showOnStartpage', 'Auf der Startseite anzeigen', '', on('showOnStartpage'))}
      ${toggleCard('go-dd-showWeight', 'Gewicht anzeigen', '', on('showWeight'))}
      ${toggleCard('go-dd-showQuantityInfo', 'Lagerbestand anzeigen', '', on('showQuantityInfo'))}
      ${toggleCard('go-dd-showPriceOffer', '„Woanders günstiger?"-Hinweis anzeigen', '', on('showPriceOffer'))}
      ${toggleCard('go-dd-showAddedDateTime', 'Einstelldatum anzeigen', '', on('showAddedDateTime'))}
      ${toggleCard('go-dd-isFsk18', 'Altersfreigabe ab 18', '', on('isFsk18'))}
      ${toggleCard('go-dd-onSitemap', 'In die Sitemap aufnehmen', '', on('onSitemap'))}
      <div class="frow" style="margin-top:14px;"><div class="field full"><label>Verfügbarkeit</label><div class="selwrap"><select class="sel" id="go-dd-priceStatus">
        ${priceOpts.map(([v, l]) => `<option value="${v}"${v === priceStatus ? ' selected' : ''}>${l}</option>`).join('')}
      </select></div></div></div>
      <div class="frow"><div class="field full"><label>Sitemap-Priorität</label><div class="selwrap"><select class="sel" id="go-dd-sitemapPriority">
        ${prioOpts.map((v) => `<option value="${v}"${v === prio ? ' selected' : ''}>${v}</option>`).join('')}
      </select></div></div></div>
      <div class="frow"><div class="field full"><label>Sitemap-Änderungsfrequenz</label><div class="selwrap"><select class="sel" id="go-dd-sitemapChangeFrequency">
        ${freqOpts.map(([v, l]) => `<option value="${v}"${v === freq ? ' selected' : ''}>${escHtml(l)}</option>`).join('')}
      </select></div></div></div>
    </div>
    <div class="actions" style="margin-top:18px;"><button class="btn btn-primary" id="go-dd-save">Speichern</button></div>`;
  const tgl = (id: string): HTMLButtonElement => {
    const el = body.querySelector<HTMLButtonElement>(id)!;
    el.onclick = () => el.classList.toggle('on');
    return el;
  };
  const sw = {
    active: tgl('#go-dd-active'),
    deactivate: tgl('#go-dd-deactivate'),
    enabled: tgl('#go-dd-enabled'),
    showWeight: tgl('#go-dd-showWeight'),
    showQuantityInfo: tgl('#go-dd-showQuantityInfo'),
    showPriceOffer: tgl('#go-dd-showPriceOffer'),
    showAddedDateTime: tgl('#go-dd-showAddedDateTime'),
    showOnStartpage: tgl('#go-dd-showOnStartpage'),
    onSitemap: tgl('#go-dd-onSitemap'),
    isFsk18: tgl('#go-dd-isFsk18'),
  };
  // Optischer Bezug zum Schalter: ist „Standardwerte bei Neu-Listung anwenden" AUS, faellt die
  // Untergruppe gedaempft zurueck. AENDERBAR bleibt sie (kein disabled, keine gesperrten Klicks) —
  // Werte vorbereiten und den Schalter danach umlegen muss moeglich bleiben.
  const ddGroup = body.querySelector<HTMLElement>('#go-dd-group');
  if (ddGroup) {
    const syncMuted = (): void => {
      ddGroup.classList.toggle('is-muted', !sw.enabled.classList.contains('on'));
    };
    // NACH dem `onclick` aus `tgl()` registriert, laeuft also erst, wenn die Klasse `on` schon
    // umgeschaltet ist.
    sw.enabled.addEventListener('click', syncMuted);
    syncMuted();
  }

  body.querySelector<HTMLButtonElement>('#go-dd-save')!.onclick = () => {
    const defaults: Record<string, unknown> = {
      enabled: sw.enabled.classList.contains('on'),
      showWeight: sw.showWeight.classList.contains('on'),
      showQuantityInfo: sw.showQuantityInfo.classList.contains('on'),
      showPriceOffer: sw.showPriceOffer.classList.contains('on'),
      showAddedDateTime: sw.showAddedDateTime.classList.contains('on'),
      showOnStartpage: sw.showOnStartpage.classList.contains('on'),
      onSitemap: sw.onSitemap.classList.contains('on'),
      isFsk18: sw.isFsk18.classList.contains('on'),
      priceStatus: Number(body.querySelector<HTMLSelectElement>('#go-dd-priceStatus')!.value) || 0,
      sitemapPriority: body.querySelector<HTMLSelectElement>('#go-dd-sitemapPriority')!.value,
      sitemapChangeFrequency: body.querySelector<HTMLSelectElement>('#go-dd-sitemapChangeFrequency')!.value,
    };
    void saveShopSettings(sctx, DEFAULT_SHOP, cur, {
      display_defaults: defaults,
      default_shipping_time_id: Number(body.querySelector<HTMLSelectElement>('#go-dd-ship')!.value) || 1,
      default_active: sw.active.classList.contains('on'),
      deactivate_on_unassign: sw.deactivate.classList.contains('on'),
    })
      .then(() => showToast('Einstellungen gespeichert.', 'success'))
      .catch((e: Error) => zeigeFehler('Speichern fehlgeschlagen', e));
  };
}

async function viewBetrieb(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const [cur, po, cursor, creds] = await Promise.all([
    loadShopSettings(sctx, DEFAULT_SHOP).then((r) => r ?? {}),
    loadPaymentOptions(sctx),
    // Best-effort: ist der Zeiger nicht lesbar, faellt nur dieser Abschnitt aus, nicht die Seite.
    loadOrderBackfillCursor(sctx, DEFAULT_SHOP).catch(() => null),
    // Nur fuer den Deeplink in die Shop-Zaehlerkreise; faellt er aus, fehlt bloss der Knopf.
    loadShopPublicCreds(sctx, DEFAULT_SHOP).catch(() => null),
  ]);
  const sub = document.getElementById('go-psub'); if (sub) sub.textContent = 'Bestellübernahme, Sync-Richtung und Standard-Beleg­werte';
  const dir = String(cur['sync_direction'] ?? 'vario_to_gambio');
  const autoTakeover = cur['auto_takeover_orders'] === true;
  const cnFromGambio = cur['customer_number_from_gambio'] === true;
  const opt = (list: Array<{ id: string; label: string }>, sel: string): string =>
    ['<option value="">— keine —</option>', ...list.map((o) => `<option value="${escHtml(o.id)}"${o.id === sel ? ' selected' : ''}>${escHtml(o.label)}</option>`)].join('');
  body.innerHTML = `
    <div class="actions" style="display:flex;justify-content:flex-end;margin-bottom:14px;"><button class="btn btn-primary" id="go-b-save">Speichern</button></div>
    <div class="frow"><div class="field full"><label>Sync-Richtung</label><div class="selwrap"><select class="sel" id="go-b-dir">
      <option value="vario_to_gambio"${dir === 'vario_to_gambio' ? ' selected' : ''}>Vario → Gambio (Vario führt)</option>
      <option value="gambio_to_vario"${dir === 'gambio_to_vario' ? ' selected' : ''}>Gambio → Vario (Gambio führt)</option>
    </select></div></div></div>
    <div class="toggle-card"><div class="tc-body"><div class="tc-t">Bestellungen automatisch nach Vario übernehmen</div><div class="tc-s">Neue Gambio-Bestellungen werden automatisch als Vario-Beleg angelegt. Übernommen werden nur Bestellungen, die <strong>nach dem Einschalten</strong> im Shop eingehen. Bereits vorhandene, ältere Bestellungen bleiben unberührt und können einzeln über das Bestelldetail übernommen werden.</div></div><button class="sw ${autoTakeover ? 'on' : ''}" id="go-b-auto"></button></div>
    ${cursor ? `
    <div class="sec-lab" style="margin-top:18px;">Bestellimport</div>
    <div class="frow">
      <div class="field"><label>Nächste zu importierende Bestellung</label>
        <input class="inp mono" id="go-b-next" inputmode="numeric" value="${cursor.nextOrderId ?? ''}" placeholder="noch nichts importiert">
      </div>
      <div class="field"><label>Bereits importiert</label>
        <input class="inp" value="${cursor.ordersImported} Bestellungen${cursor.highestImported !== null ? `, bis Nr. ${cursor.highestImported}` : ''}" disabled>
      </div>
    </div>
    <div class="hint">Der Wert wandert beim Import automatisch weiter. <strong>Höher setzen</strong> überspringt alle älteren Bestellungen — nützlich bei einem Shop mit langer Historie, aus der nichts übernommen werden soll. <strong>Zurücksetzen</strong> holt die betroffenen Bestellungen <strong>erneut vollständig</strong>, inklusive Positionen.</div>
    ` : ''}
    <div class="sec-lab" style="margin-top:18px;">Kundennummern</div>
    <div class="toggle-card"><div class="tc-body"><div class="tc-t">Kundennummer aus dem Shop übernehmen</div><div class="tc-s">Neu angelegte Kunden tragen in VARIO die Kundennummer aus dem Gambio-Shop. Ist der Schalter aus, vergibt VARIO die Nummer selbst.</div></div><button class="sw ${cnFromGambio ? 'on' : ''}" id="go-b-cnfrom"></button></div>
    <div class="hint">Dafür muss der VARIO-Zählerkreis für Kundennummern auf <strong>„automatisch, aber abänderbar"</strong> stehen. Vergibt VARIO die Nummern automatisch und nicht abänderbar, muss der Schalter aus bleiben: VARIO weist jede mitgelieferte Nummer ab und die Auftragsübernahme scheitert.<br>Die Gambio-Kundennummer muss außerdem <strong>genau so lang</strong> sein wie im VARIO-Zählerkreis eingestellt und darf dort noch <strong>keinem anderen Kunden</strong> gehören. Damit sich die beiden Nummernkreise nicht überschneiden, den initialen Wert in VARIO oberhalb des Bereichs setzen, in dem die Gambio-Kundennummern liegen.</div>
    ${varioCountersUrl() || gambioIdStartsAdminUrl(creds?.base_url ?? null) ? `<div class="map-toolbar">${varioCountersUrl() ? `<a class="btn btn-ghost btn-sm" href="${escHtml(varioCountersUrl()!)}" target="_blank" rel="noopener">Zählerkreise in VARIO öffnen</a>` : ''}${gambioIdStartsAdminUrl(creds?.base_url ?? null) ? `<a class="btn btn-ghost btn-sm" href="${escHtml(gambioIdStartsAdminUrl(creds?.base_url ?? null)!)}" target="_blank" rel="noopener">Zählerkreise in Gambio öffnen</a>` : ''}</div>` : ''}
    <div class="sec-lab" style="margin-top:18px;">Standard-Belegwerte (Fallback ohne Zuordnung)</div>
    <div class="frow"><div class="field"><label>Zahlart</label><div class="selwrap"><select class="sel" id="go-b-pm">${opt(po.methods, String(cur['vario_payment_method_id'] ?? ''))}</select></div></div>
      <div class="field"><label>Zahlungsbedingung</label><div class="selwrap"><select class="sel" id="go-b-pt">${opt(po.terms, String(cur['vario_payment_term_id'] ?? ''))}</select></div></div></div>
    <div class="frow"><div class="field"><label>Versandart</label><div class="selwrap"><select class="sel" id="go-b-dm">${opt(po.deliveryMethods, String(cur['vario_delivery_method_id'] ?? ''))}</select></div></div>
      <div class="field"><label>Versandbedingung</label><div class="selwrap"><select class="sel" id="go-b-dt">${opt(po.deliveryTerms, String(cur['vario_delivery_term_id'] ?? ''))}</select></div></div></div>
`;
  const auto = body.querySelector<HTMLButtonElement>('#go-b-auto')!;
  auto.onclick = () => auto.classList.toggle('on');
  const cnFrom = body.querySelector<HTMLButtonElement>('#go-b-cnfrom')!;
  cnFrom.onclick = () => cnFrom.classList.toggle('on');

  const selVal = (id: string): { id: string | null; label: string | null } => {
    const el = body.querySelector<HTMLSelectElement>(id)!;
    const v = el.value || null;
    return { id: v, label: v ? (el.options[el.selectedIndex]?.text ?? null) : null };
  };
  // EIN Speichern-Knopf fuer die ganze Seite (oben rechts). Er schreibt die Shop-Einstellungen UND
  // die naechste zu importierende Bestellnummer, obwohl dahinter zwei verschiedene RPCs liegen: der
  // Zeiger laeuft absichtlich NICHT ueber `gambio_set_shop_settings` (26 benannte Argumente,
  // Overload-Historie, Migration 0259), sondern ueber `gambio_set_order_backfill_cursor`
  // (Migration 0335). Das ist eine Frage der Technik dahinter, kein Grund, dem Anwender zwei
  // Knoepfe hinzustellen.
  const saveBtn = body.querySelector<HTMLButtonElement>('#go-b-save')!;
  saveBtn.onclick = () => {
    const pm = selVal('#go-b-pm'); const pt = selVal('#go-b-pt'); const dm = selVal('#go-b-dm'); const dt = selVal('#go-b-dt');

    // Bestellnummer nur schreiben, wenn sie WIRKLICH geaendert wurde. Ein Schreiben mit dem
    // gleichen Wert wuerde den Delta-Cursor leeren und damit einen kompletten Voll-Abgleich
    // ausloesen — bei einem Shop mit langer Historie eine teure Nebenwirkung des Speichern-Knopfs.
    const nextEl = body.querySelector<HTMLInputElement>('#go-b-next');
    let nextOrderId: number | null = null;
    if (nextEl) {
      const raw = nextEl.value.trim();
      const n = Number(raw);
      if (raw && (!Number.isInteger(n) || n < 1)) {
        showToast('Bitte eine positive Bestellnummer eintragen.', 'error');
        return;
      }
      if (raw && n !== cursor?.nextOrderId) nextOrderId = n;
    }

    saveBtn.disabled = true;
    void saveShopSettings(sctx, DEFAULT_SHOP, cur, {
      sync_direction: body.querySelector<HTMLSelectElement>('#go-b-dir')!.value || 'vario_to_gambio',
      auto_takeover_orders: auto.classList.contains('on'),
      customer_number_from_gambio: cnFrom.classList.contains('on'),
      vario_payment_method_id: pm.id, vario_payment_method_label: pm.label,
      vario_payment_term_id: pt.id, vario_payment_term_label: pt.label,
      vario_delivery_method_id: dm.id, vario_delivery_method_label: dm.label,
      vario_delivery_term_id: dt.id, vario_delivery_term_label: dt.label,
    })
      .then(() => (nextOrderId === null ? null : saveOrderBackfillCursor(sctx, DEFAULT_SHOP, nextOrderId)))
      .then(() => showToast(
        nextOrderId === null
          ? 'Einstellungen gespeichert.'
          : `Einstellungen gespeichert. Bestellimport beginnt künftig bei Nr. ${nextOrderId}.`,
        'success',
      ))
      .catch((e: Error) => zeigeFehler('Speichern fehlgeschlagen', e))
      .finally(() => { saveBtn.disabled = false; });
  };
}

async function viewKundenexport(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const cur = (await loadShopSettings(sctx, DEFAULT_SHOP)) ?? {};
  const sub = document.getElementById('go-psub'); if (sub) sub.textContent = 'Kundenkonten zwischen Gambio und Vario übertragen';
  const enabled = cur['export_customers_enabled'] === true;
  const createNew = cur['export_customers_create_new'] === true;
  body.innerHTML = `
    <div class="toggle-card"><div class="tc-body"><div class="tc-t">Kunden nach Gambio exportieren</div><div class="tc-s">Vorgemerkte Vario-Kunden werden regelmäßig nach Gambio übertragen.</div></div><button class="sw ${enabled ? 'on' : ''}" id="go-ke-enabled"></button></div>
    <div class="toggle-card"><div class="tc-body"><div class="tc-t">Auch neue Vario-Kunden anlegen</div><div class="tc-s">Beim Export in Gambio noch nicht vorhandene Kunden neu anlegen.</div></div><button class="sw ${createNew ? 'on' : ''}" id="go-ke-createnew"></button></div>
    <div class="sec-lab" style="margin-top:22px;">Manuelle Übertragung</div>
    <div class="actions"><button class="btn btn-ghost" id="go-ke-import">Kunden aus Gambio importieren</button><button class="btn btn-ghost" id="go-ke-export">Kunden nach Gambio exportieren</button></div>`;
  const swEnabled = body.querySelector<HTMLButtonElement>('#go-ke-enabled')!;
  const swCreate = body.querySelector<HTMLButtonElement>('#go-ke-createnew')!;
  // Beide Schalter speichern sofort, immer beide Werte zusammen (siehe autoSaveSwitch).
  const saveBoth = (): Promise<void> => saveShopSettings(sctx, DEFAULT_SHOP, cur, {
    export_customers_enabled: swEnabled.classList.contains('on'),
    export_customers_create_new: swCreate.classList.contains('on'),
  });
  for (const el of [swEnabled, swCreate]) autoSaveSwitch(el, saveBoth);
  body.querySelector<HTMLButtonElement>('#go-ke-import')!.onclick = (e) => {
    const btn = e.currentTarget as HTMLButtonElement; btn.disabled = true;
    void enqueueCustomerImport(sctx, DEFAULT_SHOP)
      .then(() => showToast('Kundenübernahme gestartet … (läuft im Hintergrund)', 'info'))
      .catch((err: Error) => zeigeFehler('Aktion fehlgeschlagen', err))
      .finally(() => { btn.disabled = false; });
  };
  body.querySelector<HTMLButtonElement>('#go-ke-export')!.onclick = (e) => {
    const btn = e.currentTarget as HTMLButtonElement; btn.disabled = true;
    void enqueueCustomerExport(sctx, DEFAULT_SHOP)
      .then(() => showToast('Kunden-Export gestartet.', 'success'))
      .catch((err: Error) => zeigeFehler('Aktion fehlgeschlagen', err))
      .finally(() => { btn.disabled = false; });
  };
}

async function viewKategorien(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const sub = document.getElementById('go-psub');
  if (sub) sub.textContent = 'Gambio-Kategorien verwalten und nach Vario spiegeln';
  await renderCategoriesEditor(body, sctx);
}

// ── Varianten-Zuordnung (Gambio-Optionen ↔ Vario-Attribute/-Werte) ────────────
// Portiert aus dem früheren Standalone-Tab in den Verkaufskanal. Je Gambio-Option
// eine Maptable (links Vario, rechts Gambio): Attribut-Zeile + Wert-Zeilen.
const VARIANT_NEW = '__new__'; // Dropdown-Sentinel „neu in Vario anlegen".

function variantChip(source: string | null): string {
  if (source === 'override') return '<span class="chip ok"><span class="d"></span>Zugeordnet</span>';
  if (source === 'auto') return '<span class="chip ok"><span class="d"></span>Auto</span>';
  return '<span class="chip miss"><span class="d"></span>offen</span>';
}

function wireVariantModesSave(body: HTMLElement, sctx: SettingsCtx, cur: Record<string, unknown>): void {
  const btn = body.querySelector<HTMLButtonElement>('#go-var-modes-save');
  if (!btn) return;
  btn.onclick = () => {
    const priceTypeVal = body.querySelector<HTMLSelectElement>('#go-var-pricetype')!.value || 'fixed';
    btn.disabled = true;
    void saveVariantModes(sctx, DEFAULT_SHOP, cur, { variantPriceTypeMode: priceTypeVal })
      .then(() => showToast('Einstellungen gespeichert.', 'success'))
      .catch((e: Error) => zeigeFehler('Speichern fehlgeschlagen', e))
      .finally(() => { btn.disabled = false; });
  };
}

/** Block „Variantenbilder": stösst den Nachtrags-Bildimport an (Produktbilder an den Variantenhauptartikel,
 *  Optionswert-Bilder an die Varianten, die den Wert tragen). Kein Bestätigungsdialog: der Lauf ist
 *  nicht destruktiv, bestehende Zuordnungen bleiben unangetastet. */
function variantImageBackfillHtml(): string {
  return `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin-top:18px;">
      <div class="sec-lab" style="margin:0;">Variantenbilder</div>
      <button class="btn btn-primary btn-sm" id="go-var-images-backfill">Bilder aus Gambio nachziehen</button>
    </div>
    <div class="hint" style="margin-bottom:14px;">Holt die Bilder aller Variantengruppen dieses Shops nach Vario:
      Produktbilder an den Variantenhauptartikel, das Bild eines Optionswerts an die Varianten, die diesen Wert tragen.
      Bereits vorhandene Zuordnungen bleiben unverändert.</div>`;
}

function wireVariantImageBackfill(body: HTMLElement, sctx: SettingsCtx): void {
  const btn = body.querySelector<HTMLButtonElement>('#go-var-images-backfill');
  if (!btn) return;
  btn.onclick = () => {
    btn.disabled = true;
    void enqueueVariantImageImport(sctx, DEFAULT_SHOP)
      .then(() => showToast('Bildimport gestartet. Die Bilder erscheinen in Vario, sobald der Lauf durch ist.', 'success'))
      .catch((e: Error) => zeigeFehler('Bildimport konnte nicht gestartet werden', e))
      .finally(() => { btn.disabled = false; });
  };
}

/** Warnbanner über den offenen Kollisionen im Warnstil der App (vgl. settings/categories-editor.ts).
 *  Leer, wenn nichts offen ist. Wird auch im Fehlerzweig des Varianten-Abschnitts ausgegeben. */
function conflictBannerHtml(rows: readonly ConflictRow[]): string {
  if (rows.length === 0) return '';
  const count = rows.length === 1 ? '1 Varianten-Konflikt' : `${rows.length} Varianten-Konflikte`;
  return `
    <div style="display:flex;align-items:center;gap:12px;margin-bottom:16px;font-size:12.5px;border-radius:9px;padding:10px 13px;background:#FDF1DC;color:#9A6A12;border:1px solid #F5DFB0;">
      <div style="flex:1;line-height:1.45;">
        <strong>${count}:</strong>
        Mehrere Gambio-Optionen zeigen auf dasselbe Vario-Attribut. Produktgruppen mit diesen Optionen
        werden beim Anlegen übersprungen, bis die Zuordnung getrennt oder die Doppelnutzung bestätigt ist.
      </div>
      <button class="btn btn-ghost btn-sm" id="go-var-conflicts" style="flex:none;">Details</button>
    </div>`;
}

/** Verdrahtet den „Details"-Button des Kollisions-Banners. Kein Auto-Modal: es öffnet nur auf Klick.
 *  `rows` ist die Liste, die auch das Banner beschreibt — nicht der Modulzustand, damit Banner und Modal
 *  denselben Stand zeigen. */
function wireConflictBanner(
  body: HTMLElement,
  sctx: SettingsCtx,
  rows: readonly ConflictRow[],
  optionNames: ReadonlyMap<string, string>,
  attributeLabels: ReadonlyMap<string, string>,
  varioAttributes: ReadonlyArray<{ id: string; label: string; values?: ReadonlyArray<{ label: string }> }>,
  valueMap: readonly ValueMapping[],
): void {
  const btn = body.querySelector<HTMLButtonElement>('#go-var-conflicts');
  if (!btn) return;
  btn.onclick = () => {
    openConflictModal(
      body.closest('.go-settings') as HTMLElement,
      sctx, rows, optionNames, attributeLabels, varioAttributes, valueMap,
    );
  };
}

/** Listet die offenen Kollisionen und erlaubt je Paar die Bestätigung „ist absichtlich dasselbe Attribut".
 *  Modal-Markup liegt außerhalb von `.go-settings` → hier `v-button` statt `btn` (das ist scoped).
 *
 *  Die Identität des Paares steckt in den data-Attributen der Zeile, NICHT in einem Index in eine Liste:
 *  das Modal hängt an `document.body` und überlebt ein `rerender()`, das die Konflikt-Liste neu befüllt
 *  (z. B. weil parallel eine Zuordnung gespeichert wurde und die Kollision damit weg ist). Über einen
 *  Index würde ein Klick danach das falsche Paar dauerhaft freigeben — ohne Fehlermeldung. */
function openConflictModal(
  host: HTMLElement,
  sctx: SettingsCtx,
  rows: readonly ConflictRow[],
  optionNames: ReadonlyMap<string, string>,
  attributeLabels: ReadonlyMap<string, string>,
  varioAttributes: ReadonlyArray<{ id: string; label: string; values?: ReadonlyArray<{ label: string }> }>,
  valueMap: readonly ValueMapping[],
): void {
  // Trennen DIREKT hier: vorher liess sich die Kollision im Modal nur bestaetigen, das Auseinander-
  // ziehen musste man sich in der Liste darunter selbst zusammensuchen — und war die Zuordnung gar
  // nicht ladbar, ging es ueberhaupt nicht (Befund des Betreibers 2026-07-29: „Ich habe nur die
  // Option 'Ist absichtlich dasselbe Attribut', ich kann es nicht auseinanderziehen").
  const canSeparate = varioAttributes.length > 0;
  const attrSelect = (optionId: string, current: string): string => {
    const opts = varioAttributes
      .map((a) => `<option value="${escHtml(a.id)}"${a.id === current ? ' selected' : ''}>${escHtml(attributeChoiceLabel(a))}</option>`)
      .join('');
    return `<div class="selwrap" style="min-width:220px;flex:none;">
      <select class="sel" data-sep-select="1" data-opt="${escHtml(optionId)}">${opts}
        <option value="${VARIANT_NEW}">➕ neu in Vario anlegen …</option>
      </select></div>`;
  };
  const separateBlock = (row: ConflictRow): string => {
    if (!canSeparate) {
      return `<div class="hint" style="margin:8px 0 2px;">Die Vario-Attribute sind gerade nicht abrufbar,
        deshalb lässt sich die Zuordnung hier nicht umstellen. Abschnitt neu laden; bleibt es dabei, ist
        der Vario-Zugang zu prüfen.</div>`;
    }
    return `<div style="margin:9px 0 2px;display:flex;flex-direction:column;gap:7px;">
      ${[row.option_id_low, row.option_id_high].map((oid) => `
        <div style="display:flex;align-items:center;gap:10px;">
          <div style="flex:1;font-size:12.5px;">Gambio-Option „${escHtml(optionNames.get(oid) ?? 'unbekannt')}" zuordnen auf</div>
          ${attrSelect(oid, row.vario_attribute_id)}
        </div>`).join('')}
      <div><button type="button" class="v-button" data-sep-save="1">Zuordnung speichern</button></div>
    </div>`;
  };
  const rowsHtml = rows.map((row) => `
    <div data-conflict-row="1" data-kind="${escHtml(row.kind)}" data-attr="${escHtml(row.vario_attribute_id)}"
         data-low="${escHtml(row.option_id_low)}" data-high="${escHtml(row.option_id_high)}"
         style="padding:11px 0;border-top:1px solid #EDEDF0;">
      <div style="font-size:12.5px;line-height:1.45;">${escHtml(conflictSentence(row, optionNames, attributeLabels))}</div>
      ${separateBlock(row)}
      <div style="margin-top:7px;">
        <button type="button" class="v-button" data-ack="1"
          data-kind="${escHtml(row.kind)}" data-attr="${escHtml(row.vario_attribute_id)}"
          data-low="${escHtml(row.option_id_low)}" data-high="${escHtml(row.option_id_high)}">Ist absichtlich dasselbe Attribut</button>
      </div>
    </div>`).join('');
  let modalOpen = true;
  const modal = openModal({
    title: 'Varianten-Konflikte',
    icon: 'fa-triangle-exclamation',
    width: 760,
    body: `<div class="kl-form-hint" style="margin-bottom:6px;">
        Zwei Gambio-Optionen auf einem Vario-Attribut vermischen zwei Varianten-Dimensionen — Produktgruppen
        mit diesen Optionen werden beim Anlegen übersprungen. Ordne eine der Optionen einem eigenen
        Vario-Attribut zu, oder bestätige, dass die Doppelnutzung so gewollt ist.
      </div>${rowsHtml}`,
    footer: '<button type="button" class="v-button" data-modal-cancel>Schließen</button>',
    onClose: () => { modalOpen = false; },
  });

  // „Zuordnung speichern": beide Auswahllisten der Zeile lesen und nur die Optionen schreiben, deren
  // Attribut sich geaendert hat. Zeigen danach beide auf verschiedene Attribute, ist die Kollision weg
  // und die Zeile verschwindet nach dem rerender von selbst — es braucht keine Quittierung.
  for (const saveBtn of Array.from(modal.el.querySelectorAll<HTMLButtonElement>('[data-sep-save]'))) {
    saveBtn.onclick = () => {
      const rowEl = saveBtn.closest<HTMLElement>('[data-conflict-row]');
      if (!rowEl) return;
      const current = rowEl.dataset['attr'] ?? '';
      const selects = Array.from(rowEl.querySelectorAll<HTMLSelectElement>('[data-sep-select]'));
      const changed = selects.filter((s) => s.value && s.value !== current);
      if (changed.length === 0) {
        showToast('Bitte mindestens eine Option auf ein anderes Attribut stellen.', 'error');
        return;
      }
      if (changed.length === selects.length && new Set(changed.map((s) => s.value)).size === 1) {
        showToast('Beide Optionen zeigen dann wieder auf dasselbe Attribut. Bitte nur eine umstellen.', 'error');
        return;
      }
      // Werte-Konsistenz: die Wert-Zuordnungen der Option muessen zum NEUEN Attribut gehoeren. Sonst
      // widersprechen sich Attribut- und Wert-Ebene, und genau daraus entsteht ein Schema, dessen
      // Wertemenge nicht zum Attribut passt — Vario legt dann STILL null Kinder an (Vorfall
      // 2026-07-29: Zuordnung vertauscht statt getrennt, 3 bzw. 11 Werte am falschen Attribut).
      // Kein Browser-Dialog (Projektregel), deshalb zweistufiger Klick: der erste erklaert, der
      // zweite verwirft die unpassenden Wert-Zuordnungen mit und speichert.
      const stale = changed.flatMap((sel) => staleValueMappings(
        valueMap,
        sel.dataset['opt'] ?? '',
        sel.value === VARIANT_NEW ? null : sel.value,
      ));
      if (stale.length > 0 && saveBtn.dataset['confirmStale'] !== '1') {
        saveBtn.dataset['confirmStale'] = '1';
        saveBtn.textContent = `${stale.length} Wert-Zuordnung(en) verwerfen und speichern`;
        showToast(
          `${stale.length} Wert-Zuordnung(en) dieser Option gehören zum bisherigen Attribut. Sie müssen `
          + 'mit umziehen, sonst passen Attribut und Werte nicht zusammen und Vario legt keine Varianten '
          + 'an. Nochmals klicken, um sie zu verwerfen — sie lassen sich danach neu zuordnen.',
          'error',
        );
        return;
      }

      saveBtn.disabled = true;
      void (async () => {
        for (const sel of changed) {
          const optionId = sel.dataset['opt'] ?? '';
          if (!optionId) continue;
          let attrId = sel.value;
          if (attrId === VARIANT_NEW) {
            attrId = await createVarioVariant(sctx, DEFAULT_SHOP, 'attribute', {
              gambioOptionId: optionId,
              label: optionNames.get(optionId) ?? `Option ${optionId}`,
            });
          }
          await setVariantOverride(sctx, DEFAULT_SHOP, { gambioOptionId: optionId, varioAttributeId: attrId });
        }
        // Erst NACH dem Umstellen verwerfen: bricht das Speichern vorher ab, bleibt der alte,
        // konsistente Zustand erhalten statt eines Attributs ohne Wert-Zuordnungen.
        for (const v of stale) {
          await clearVariantOverride(sctx, DEFAULT_SHOP, v.gambioOptionId, v.gambioOptionValueId);
        }
      })()
        .then(() => {
          showToast('Zuordnung gespeichert.', 'success');
          modal.close();
          void rerender(host);
        })
        .catch((e: Error) => {
          zeigeFehler('Speichern fehlgeschlagen', e);
          if (modalOpen) saveBtn.disabled = false;
        });
    };
  }

  const ackButtons = (): HTMLButtonElement[] => Array.from(modal.el.querySelectorAll<HTMLButtonElement>('[data-ack]'));
  for (const btn of ackButtons()) {
    btn.onclick = () => {
      const row: ConflictRow = {
        kind: btn.dataset['kind'] ?? 'attribute_collision',
        vario_attribute_id: btn.dataset['attr'] ?? '',
        option_id_low: btn.dataset['low'] ?? '',
        option_id_high: btn.dataset['high'] ?? '',
      };
      // ALLE Buttons sperren: sonst laufen zwei Quittierungen parallel und die zweite feuert Toast und
      // rerender in ein Modal, das die erste vielleicht schon geschlossen hat.
      for (const b of ackButtons()) b.disabled = true;
      void acknowledgeVariantShare(sctx, DEFAULT_SHOP, row)
        .then(() => {
          // Nur die betroffene Zeile raus — drei kollidierende Optionen ergeben drei Paare, der Betreiber
          // soll sie nacheinander abarbeiten können, ohne „Details" jedes Mal neu zu öffnen.
          btn.closest('[data-conflict-row]')?.remove();
          showToast('Doppelnutzung bestätigt.', 'success');
          if (ackButtons().length === 0) modal.close();
          void rerender(host);
        })
        .catch((e: Error) => {
          zeigeFehler('Bestätigen fehlgeschlagen', e);
        })
        .finally(() => {
          if (!modalOpen) return; // Modal weg → keine Buttons reaktivieren.
          for (const b of ackButtons()) b.disabled = false;
        });
    };
  }
}

async function viewVarianten(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const sub = document.getElementById('go-psub');
  if (sub) sub.textContent = 'Gambio-Varianten-Optionen den Vario-Attributen/-Werten zuordnen';

  // Konflikte parallel mitladen (nicht seriell davor) — Banner und Marker zeigen so denselben Stand.
  // Token VOR dem Promise.all ziehen, damit eine verspätete alte Antwort die Marker nicht zurückdreht.
  const seq = ++conflictsSeq;
  const [curSettings, dataResult, conflicts] = await Promise.all([
    loadShopSettings(sctx, DEFAULT_SHOP).then((r) => r ?? {}),
    loadVariantMatrix(sctx, DEFAULT_SHOP).then((d): { ok: true; data: VariantMatrix } | { ok: false; error: Error } => ({ ok: true, data: d }))
      .catch((e: Error): { ok: true; data: VariantMatrix } | { ok: false; error: Error } => ({ ok: false, error: e })),
    loadVariantConflicts(sctx, DEFAULT_SHOP),
  ]);
  // Veralteten Durchgang KOMPLETT abbrechen, nicht nur die Marker überspringen: sonst gewinnen die
  // Marker garantiert der neuere Durchgang, das Panel-`body` aber der zuletzt landende — Banner und
  // Marker könnten dann kurz auseinanderstehen. Tragfähig, weil `conflictsSeq` nur von einem
  // neueren Varianten-Durchgang oder von einem Refresh für einen ANDEREN Abschnitt erhöht wird, und
  // in beiden Fällen schreibt ohnehin ein neuerer Durchgang den Panel-Inhalt.
  if (seq !== conflictsSeq) return;
  variantConflicts = conflicts;
  const settingsHost = body.closest('.go-settings') as HTMLElement | null;
  if (settingsHost) { renderNav(settingsHost); renderConflictFlag(settingsHost); }

  if (!dataResult.ok) {
    // Banner MIT ausgeben: sonst melden Nav-Marker und Kopf-Indikator offene Konflikte, der Abschnitt
    // zeigt aber nur den Ladefehler — genau die Sackgasse, die dieses Feature beseitigen soll. Ohne
    // Matrix nennt `conflictSentence` die Namen „unbekannt" (NIE die rohen IDs, damit steht dort
    // nichts, womit ein Anwender nichts anfangen kann), und das Modal sagt selbst, dass sich die
    // Zuordnung gerade nicht umstellen laesst.
    body.innerHTML = conflictBannerHtml(conflicts)
      + `<div class="hint">Varianten-Zuordnung nicht abrufbar: ${escHtml(dataResult.error.message)}
         — solange das so ist, lassen sich Konflikte nur bestätigen, nicht trennen.</div>`;
    wireConflictBanner(body, sctx, conflicts, new Map(), new Map(), [], []);
    return;
  }
  const data = dataResult.data;

  const priceTypeMode = String(curSettings['variant_price_type_mode'] ?? 'fixed');
  const modesHtml = `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;">
      <div class="sec-lab" style="margin:0;">Varianten-Berechnung</div>
      <button class="btn btn-primary btn-sm" id="go-var-modes-save">Speichern</button>
    </div>
    <div class="frow"><div class="field full"><label>Varianten-Preistyp</label><div class="selwrap"><select class="sel" id="go-var-pricetype">
      <option value="fixed"${priceTypeMode === 'fixed' ? ' selected' : ''}>Artikelvarianten-Festpreis</option>
      <option value="from_options"${priceTypeMode === 'from_options' ? ' selected' : ''}>Preis aus Artikeloptionen berechnen</option>
    </select></div></div></div>
    <div class="frow"><div class="field full"><label>Varianten-Gewicht</label>
    <div class="hint">Gambio addiert Gewichte immer: Artikelgewicht + Variantengewicht + gewählte Optionen.
    Übertragen wird deshalb der Aufschlag zum Artikelgewicht — hier ist nichts einzustellen.</div>
    </div></div>`;

  const dirLabel = data.direction === 'gambio_to_vario' ? 'Gambio → Vario (Gambio führt)' : 'Vario → Gambio (Vario führt)';

  function attrOptions(selectedId: string | null, gambioName: string): string {
    const opts = (data.varioAttributes ?? []).map((a) => {
      const sel = a.id === selectedId ? ' selected' : '';
      const hint = a.label.toLowerCase() === gambioName.toLowerCase() ? ' (Vorschlag)' : '';
      return `<option value="${escHtml(a.id)}"${sel}>${escHtml(attributeChoiceLabel(a))}${hint}</option>`;
    }).join('');
    return `<option value="">— bitte zuordnen —</option>${opts}<option value="${VARIANT_NEW}">➕ neu in Vario anlegen …</option>`;
  }

  function valueOptions(attrId: string | null, selectedId: string | null, gambioModelNo: string): string {
    const att = (data.varioAttributes ?? []).find((a) => a.id === attrId);
    const opts = (att?.values ?? []).map((v) => {
      const sel = v.id === selectedId ? ' selected' : '';
      const hint = gambioModelNo && v.abbreviation.toLowerCase() === gambioModelNo.toLowerCase() ? ' (Vorschlag)' : '';
      return `<option value="${escHtml(v.id)}"${sel}>${escHtml(v.label)}${hint}</option>`;
    }).join('');
    return `<option value="">— bitte zuordnen —</option>${opts}<option value="${VARIANT_NEW}">➕ neu in Vario anlegen …</option>`;
  }

  function renderOption(opt: GambioVariantOption): string {
    const attrMap = (data.attributeMap ?? []).find((m) => m.gambioOptionId === opt.optionId);
    const attrSel = attrMap?.varioAttributeId ?? null;
    const sk = escHtml(opt.optionId);
    // Attribut-Zeile + Wert-Zeilen als eine Maptable (links Vario / rechts Gambio).
    const attrRow = `<tr data-attr-row="1">
      <td><div class="selwrap"><select class="sel" data-role="attr" data-opt="${sk}" data-name="${escHtml(opt.name)}">${attrOptions(attrSel, opt.name)}</select></div></td>
      ${arrowCell()}
      <td class="g-cell"><div class="v-cell"><span class="vn"><strong>${escHtml(opt.name)}</strong></span><span class="vid">Option</span></div></td>
      <td class="st-cell">${variantChip(attrMap?.source ?? null)}</td>
      <td>${attrMap ? `<button class="btn btn-ghost btn-sm" data-act="clear-attr" data-opt="${sk}">Zuordnung entfernen</button>` : ''}</td>
    </tr>`;
    const valueRows = opt.values.map((val) => {
      const vm = (data.valueMap ?? []).find((m) => m.gambioOptionValueId === val.ovid);
      const ov = escHtml(val.ovid);
      return `<tr data-ovid="${ov}">
        <td><div class="selwrap"><select class="sel" data-role="value" data-opt="${sk}" data-ovid="${ov}" data-model="${escHtml(val.modelNumber)}">${valueOptions(attrSel, vm?.varioValueId ?? null, val.modelNumber)}</select></div></td>
        ${arrowCell()}
        <td class="g-cell"><div class="v-cell"><span class="vn">${escHtml(val.label)}</span>${val.modelNumber ? `<span class="vid mono">${escHtml(val.modelNumber)}</span>` : ''}</div></td>
        <td class="st-cell">${variantChip(vm?.source ?? null)}</td>
        <td>${vm ? `<button class="btn btn-ghost btn-sm" data-act="clear-value" data-opt="${sk}" data-ovid="${ov}">Zuordnung entfernen</button>` : ''}</td>
      </tr>`;
    }).join('');
    return `<div class="gv-dim" data-opt="${sk}" style="margin-bottom:26px;">
      <div class="sec-lab" style="display:flex;align-items:center;gap:10px;">
        <span>Gambio-Option „${escHtml(opt.name)}"</span>
        <button class="btn btn-primary btn-sm" data-act="save" data-opt="${sk}" style="margin-left:auto;">Zuordnung speichern</button>
      </div>
      ${maptableShell('Vario-Attribut / -Wert', 'Gambio-Option / -Wert', attrRow + valueRows, true)}
    </div>`;
  }

  // Direktlink in die Vario-Variantenverwaltung (Attribute/Werte/Schemata) — nur im Vario-iframe
  // ermittelbar (sonst null → kein Link). Neuer Tab via <a target="_blank"> (iframe blockt window.open).
  const varioAdminUrl = varioVariantAdminUrl();
  const varioLinkHtml = varioAdminUrl
    ? `<div style="display:flex;justify-content:flex-end;margin:0 0 12px;"><a class="btn btn-ghost btn-sm" href="${escHtml(varioAdminUrl)}" target="_blank" rel="noopener"><span class="v-icon fa-light fa-arrow-up-right-from-square"></span>&nbsp;Variantenverwaltung in Vario öffnen</a></div>`
    : '';

  // Klartext-Namen für die Kollisions-Hinweise kommen aus der Matrix, die hier ohnehin geladen ist —
  // kein zusätzlicher Netz-Aufruf, keine zweite Ablage der Labels.
  const optionNames = new Map<string, string>((data.gambioOptions ?? []).map((o) => [o.optionId, o.name]));
  const attributeLabels = new Map<string, string>((data.varioAttributes ?? []).map((a) => [a.id, a.label]));
  const bannerHtml = conflictBannerHtml(conflicts);

  if ((data.gambioOptions ?? []).length === 0) {
    // Bild-Nachtrag AUCH hier anbieten: Variantengruppen aus früheren Importen können bestehen,
    // auch wenn Gambio aktuell keine Optionen (mehr) liefert.
    body.innerHTML = varioLinkHtml + bannerHtml + modesHtml + variantImageBackfillHtml()
      + `<div class="hint">Keine Gambio-Varianten-Optionen gefunden. Lege in Gambio Optionen an oder importiere Varianten-Produkte.</div>`;
    wireVariantModesSave(body, sctx, curSettings);
    wireVariantImageBackfill(body, sctx);
    wireConflictBanner(body, sctx, conflicts, optionNames, attributeLabels, data.varioAttributes ?? [], data.valueMap ?? []);
    return;
  }

  body.innerHTML = varioLinkHtml + bannerHtml + modesHtml + variantImageBackfillHtml()
    + `<div class="hint" style="margin-bottom:14px;">Richtung: <strong>${escHtml(dirLabel)}</strong> - ändern unter „Bestellungen &amp; Sync".</div>`
    + data.gambioOptions.map(renderOption).join('');
  wireVariantModesSave(body, sctx, curSettings);
  wireVariantImageBackfill(body, sctx);
  wireConflictBanner(body, sctx, conflicts, optionNames, attributeLabels, data.varioAttributes ?? [], data.valueMap ?? []);

  function selVal(role: string, optId: string, ovid?: string): string {
    const q = ovid
      ? `select[data-role="${role}"][data-opt="${CSS.escape(optId)}"][data-ovid="${CSS.escape(ovid)}"]`
      : `select[data-role="${role}"][data-opt="${CSS.escape(optId)}"]`;
    return body.querySelector<HTMLSelectElement>(q)?.value ?? '';
  }

  async function saveOption(optId: string): Promise<void> {
    const opt = data.gambioOptions.find((o) => o.optionId === optId);
    if (!opt) return;
    showToast('Zuordnung wird gespeichert …', 'info');
    try {
      let attrId = selVal('attr', optId);
      if (attrId === VARIANT_NEW) {
        attrId = await createVarioVariant(sctx, DEFAULT_SHOP, 'attribute', { gambioOptionId: optId, label: opt.name });
      }
      if (!attrId) { showToast('Bitte zuerst ein Vario-Attribut wählen.', 'error'); return; }

      for (const val of opt.values) {
        const valId = selVal('value', optId, val.ovid);
        if (valId === VARIANT_NEW) {
          await createVarioVariant(sctx, DEFAULT_SHOP, 'value', {
            gambioOptionId: optId, gambioOptionValueId: val.ovid, varioAttributeId: attrId,
            label: val.label, abbreviation: val.modelNumber || val.label,
          });
          continue; // Anlage etabliert die Zuordnung serverseitig.
        }
        if (!valId) continue;
        await setVariantOverride(sctx, DEFAULT_SHOP, {
          gambioOptionId: optId, varioAttributeId: attrId, gambioOptionValueId: val.ovid, varioValueId: valId,
        });
      }
      await setVariantOverride(sctx, DEFAULT_SHOP, { gambioOptionId: optId, varioAttributeId: attrId });
      showToast('Zuordnung gespeichert.', 'success');
      void rerender(body.closest('.go-settings') as HTMLElement);
    } catch (e) {
      zeigeFehler('Speichern fehlgeschlagen', e);
    }
  }

  async function clearOverride(optId: string, ovid?: string): Promise<void> {
    try {
      await clearVariantOverride(sctx, DEFAULT_SHOP, optId, ovid);
      showToast('Zuordnung entfernt.', 'success');
      void rerender(body.closest('.go-settings') as HTMLElement);
    } catch (e) {
      zeigeFehler('Entfernen fehlgeschlagen', e);
    }
  }

  // Attribut-Wechsel → Wert-Dropdowns dieser Option neu befüllen.
  body.addEventListener('change', (e) => {
    const sel = e.target as HTMLSelectElement;
    if (sel.dataset['role'] !== 'attr') return;
    const optId = sel.dataset['opt']!;
    const dim = body.querySelector<HTMLElement>(`.gv-dim[data-opt="${CSS.escape(optId)}"]`);
    if (!dim) return;
    const attrId = sel.value === VARIANT_NEW ? null : (sel.value || null);
    for (const vs of dim.querySelectorAll<HTMLSelectElement>('select[data-role="value"]')) {
      vs.innerHTML = valueOptions(attrId, null, vs.dataset['model'] ?? '');
    }
  });

  body.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-act]');
    if (!btn) return;
    const optId = btn.dataset['opt']!;
    switch (btn.dataset['act']) {
      case 'save': void saveOption(optId); break;
      case 'clear-attr': void clearOverride(optId); break;
      case 'clear-value': void clearOverride(optId, btn.dataset['ovid']!); break;
    }
  });
}

// ── Artikel-Mapping (global je Mandant) ──────────────────────────────────────
// Zentrales Feld-Mapping (Titel/Beschreibung/Preis/EAN/SKU/Hersteller) - gilt für
// ALLE Shops dieses Kanals, daher genau EINE Sektion, nicht pro Shop. Editor lebt
// in tabs/article-mapping.ts (eigenständiges Modul, RPC-basiert).
async function viewArtikelMapping(body: HTMLElement, sctx: SettingsCtx, shopKey: string): Promise<void> {
  const sub = document.getElementById('go-psub');
  if (sub) sub.textContent = 'Globales Feld-Mapping - gilt für alle Shops dieses Kanals';
  body.innerHTML = `<div id="gambio-article-mapping">Lade …</div>`;
  const amHost = body.querySelector<HTMLElement>('#gambio-article-mapping');
  if (amHost) await renderArticleMapping(amHost, {
    supabase: sctx.supabase, supabaseUrl: sctx.supabaseUrl, session: { token: sctx.session.token }, storefront: shopKey,
  });
}

// Zusatzfeld-Katalog (Definitionen anlegen/umbenennen/löschen) - shop-global,
// analog viewArtikelMapping ein eigenständiges Modul (settings/additional-fields-admin.ts).
async function viewZusatzfelder(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const sub = document.getElementById('go-psub');
  if (sub) sub.textContent = 'Gambio-Zusatzfelder anlegen, umbenennen oder löschen';
  body.innerHTML = `<div id="gambio-additional-fields-admin">Lade …</div>`;
  const afHost = body.querySelector<HTMLElement>('#gambio-additional-fields-admin');
  if (afHost) await renderAdditionalFieldsAdmin(afHost, { supabaseUrl: sctx.supabaseUrl, session: sctx.session });
}

async function viewAblaeufe(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const sub = document.getElementById('go-psub'); if (sub) sub.textContent = 'Automatische Synchronisationen im Hintergrund - wann was läuft';
  let crons: Array<{ jobname: string; schedule: string; active: boolean }> = [];
  try {
    const { data, error } = await sctx.supabase.rpc('gambio_list_sync_crons');
    if (error) throw error;
    crons = (data ?? []) as typeof crons;
  } catch {
    body.innerHTML = `<div class="hint">Ablauf-Zeitpläne konnten nicht geladen werden.</div>`;
    return;
  }
  if (crons.length === 0) {
    body.innerHTML = `<div class="hint">Keine automatischen Abläufe gefunden.</div>`;
    return;
  }
  const cadenceBadge = 'white-space:nowrap;font-weight:600;font-size:13px;color:#2b6cb0;background:#eaf2fb;border-radius:999px;padding:5px 12px;';
  const cards = crons.map((c) => {
    const meta = CRON_LABELS[c.jobname] ?? { label: humanizeCronJobname(c.jobname), hint: '' };
    const cadence = describeCronSchedule(c.schedule);
    const paused = c.active ? '' : ` · <span style="color:#c0392b;">pausiert</span>`;
    return `<div class="toggle-card">
      <div class="tc-body"><div class="tc-t">${escHtml(meta.label)}</div><div class="tc-s">${escHtml(meta.hint)}${paused}</div></div>
      <span style="${cadenceBadge}">${escHtml(cadence)}</span></div>`;
  }).join('');
  body.innerHTML = `${cards}<div class="hint" style="margin-top:10px;">Diese Abläufe laufen automatisch - keine manuelle Aktion nötig.</div>`;
}

/**
 * Zeitpunkt des letzten automatischen Länder-Abgleichs (Modulzustand, lebt so lange wie die
 * geladene Seite). Der Abgleich läuft beim Öffnen des Abschnitts von selbst — der Anwender soll
 * keinen Knopf suchen müssen. Die Sperre verhindert, dass jedes Hin- und Herklicken zwischen den
 * Abschnitten einen neuen Worker-Job auslöst.
 */
let laenderAutoSyncAt = 0;
const LAENDER_AUTO_SYNC_MIN_GAP_MS = 5 * 60_000;

async function viewLaender(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const sub = document.getElementById('go-psub');
  if (sub) sub.textContent = 'Automatisch synchronisierte Länder (iso2 → Gambio-Land)';

  const loadRows = async (): Promise<CountryRow[]> => {
    try { return await loadCountryMap(sctx, DEFAULT_SHOP); } catch { return []; }
  };
  const paint = (rows: CountryRow[], status: string): void => {
    const tbl = rows.map((r) => `<tr>
      <td><div class="v-cell"><span class="vn">${escHtml(r.name ?? r.iso2)}</span><span class="vid">${escHtml(r.iso2)}</span></div></td>
      ${arrowCell()}
      <td class="g-cell"><div class="v-cell"><span class="vn">${escHtml(r.name ?? r.iso2)}</span><span class="vid">${escHtml(r.iso2)}</span></div></td>
      <td class="st-cell">${chip(true)}</td></tr>`).join('');
    body.innerHTML = `
      <div class="map-toolbar"><span class="map-stat"><b>${rows.length}</b> Länder synchronisiert</span>
        <span class="hint" style="margin-left:auto;">${escHtml(status)}</span></div>
      ${rows.length ? maptableShell('Vario-Land (ISO)', 'Gambio-Land (ISO)', tbl) : '<div class="hint">Noch keine Länder übernommen. Der Abgleich läuft gerade oder wird beim nächsten Öffnen wiederholt; zusätzlich läuft er automatisch beim Bestellimport.</div>'}`;
  };

  const rows = await loadRows();
  const now = Date.now();
  const due = now - laenderAutoSyncAt > LAENDER_AUTO_SYNC_MIN_GAP_MS;
  paint(rows, due ? 'Abgleich läuft …' : 'Automatisch abgeglichen');
  if (!due) return;

  // Abgleich anstossen und abwarten, danach NUR die Tabelle neu zeichnen. Bewusst kein
  // `rerender` — das würde diese Funktion erneut aufrufen und sich im Kreis drehen.
  laenderAutoSyncAt = now;
  void (async () => {
    try {
      const jobId = await enqueueCountrySync(sctx, DEFAULT_SHOP);
      if (jobId) await awaitJob(sctx.supabase, jobId, 90_000);
      paint(await loadRows(), 'Gerade abgeglichen');
    } catch (err) {
      // Der Abgleich ist eine Bequemlichkeit, kein Selbstzweck: schlägt er fehl, bleibt die
      // vorhandene Liste stehen und sagt, was los ist. Beim nächsten Öffnen wird erneut versucht.
      laenderAutoSyncAt = 0;
      paint(await loadRows(), `Abgleich fehlgeschlagen: ${(err as Error).message}`);
    }
  })();
}

async function viewModalityMapping(body: HTMLElement, sctx: SettingsCtx, kind: 'payment'|'shipping'): Promise<void> {
  const [discovered, mappings, opts, shop, creds] = await Promise.all([
    discoverModalities(sctx, DEFAULT_SHOP, kind).catch(() => []),
    loadChannelMappings(sctx, DEFAULT_SHOP, kind).catch(() => []),
    loadPaymentOptions(sctx),
    loadShopSettings(sctx, DEFAULT_SHOP),
    // Best-effort: nur fuer den Deeplink ins Shop-Backend. Fehlt die Basis-Adresse, entfaellt der
    // Link, die Zuordnung funktioniert unveraendert.
    loadShopPublicCreds(sctx, DEFAULT_SHOP).catch(() => null),
  ]);
  const methods = kind === 'payment' ? opts.methods : opts.deliveryMethods;
  const terms   = kind === 'payment' ? opts.terms   : opts.deliveryTerms;
  const cur = shop ?? {};
  const defMethodId = String((kind==='payment'? cur['vario_payment_method_id'] : cur['vario_delivery_method_id']) ?? '');
  const defTermId   = String((kind==='payment'? cur['vario_payment_term_id']   : cur['vario_delivery_term_id'])   ?? '');
  // Zeilen = Vereinigung entdeckter + gemappter Module (dedup per module).
  const mapByModule = new Map(mappings.map((m) => [m.gambioModule, m]));
  const modules = new Map<string, { label: string | null }>();
  for (const d of discovered) modules.set(d.gambioModule, { label: d.sampleLabel });
  for (const m of mappings) if (!modules.has(m.gambioModule)) modules.set(m.gambioModule, { label: m.gambioLabel });
  const sub = document.getElementById('go-psub');
  const done = [...modules.keys()].filter((k) => mapByModule.get(k)?.varioMethodId).length;
  if (sub) sub.textContent = `${done}/${modules.size} zugeordnet`;
  setOpenCount(body.closest('.go-settings') as HTMLElement, kind === 'payment' ? 'zahlungsarten' : 'versandarten', modules.size - done);
  if (modules.size === 0) { body.innerHTML = `<div class="hint">Noch keine ${kind==='payment'?'Zahlarten':'Versandarten'} in Bestellungen gesehen. Sie erscheinen hier, sobald Bestellungen importiert wurden.</div>`; return; }
  const sel = (name: 'm'|'t', options: Array<{id:string;label:string}>, selectedId: string): string =>
    `<div class="selwrap"><select class="sel" data-f="${name}">${['<option value="">— bitte wählen —</option>', ...options.map((o)=>`<option value="${escHtml(o.id)}"${o.id===selectedId?' selected':''}>${escHtml(o.label)}</option>`)].join('')}</select></div>`;
  const rows = [...modules.entries()].map(([mod, info]) => {
    const cm = mapByModule.get(mod);
    const mId = cm?.varioMethodId ?? (cm ? '' : defMethodId);
    const tId = cm?.varioTermId ?? (cm ? '' : defTermId);
    const label = cm?.gambioLabel ?? info.label ?? mod;
    return `<tr data-mod="${escHtml(mod)}" data-label="${escHtml(label ?? '')}">
      <td style="min-width:190px;">${sel('m', methods, mId)}</td>
      <td style="min-width:190px;">${sel('t', terms, tId)}</td>
      ${arrowCell()}
      <td class="g-cell"><div class="v-cell"><span class="vn">${escHtml(label ?? mod)}</span><span class="vid">${escHtml(mod)}</span></div></td>
      <td class="st-cell">${chip(!!cm?.varioMethodId)}</td>
      <td><button class="btn btn-primary btn-sm" data-save>Speichern</button></td>
    </tr>`;
  }).join('');
  // Direktlinks zu den beiden Verwaltungen: links wird gepflegt, was in den Dropdowns steht,
  // rechts, was der Shop meldet. Erspart die Suche in zwei Systemen. Nur bei den Zahlungsarten —
  // fuer die Versandarten sind die Routen nicht belegt, und geratene Links sind schlimmer als
  // keine. Kein `window.open`: die App laeuft im Vario-iframe ohne allow-popups.
  // Routen beider Systeme vom Betreiber genannt, nichts geraten:
  //   Zahlungsarten  VARIO  /settings/finances/paymentModalities
  //                  Gambio /admin/admin.php?do=HubConfiguration/PaymentMethods
  //   Versandarten   VARIO  /settings/stocks/deliveryModalities
  //                  Gambio /admin/modules.php?set=shipping   (dieselbe Seite wie bei den
  //                         Paketdiensten — in Gambio sind Versandart und Versandmodul dasselbe)
  const varioAdmin = kind === 'payment' ? varioPaymentModalitiesUrl() : varioDeliveryModalitiesUrl();
  const shopAdmin = kind === 'payment'
    ? gambioPaymentMethodsAdminUrl(creds?.base_url ?? null)
    : gambioShippingModulesAdminUrl(creds?.base_url ?? null);
  const was = kind === 'payment' ? 'Zahlungsarten' : 'Versandarten';
  const shopWas = kind === 'payment' ? 'Zahlungsarten' : 'Versandmodule';
  const linkBar = varioAdmin || shopAdmin
    ? `<div class="map-toolbar">
        ${varioAdmin ? `<a class="btn btn-ghost btn-sm" href="${escHtml(varioAdmin)}" target="_blank" rel="noopener">${was} in VARIO öffnen</a>` : ''}
        ${shopAdmin ? `<a class="btn btn-ghost btn-sm" href="${escHtml(shopAdmin)}" target="_blank" rel="noopener">${shopWas} in Gambio öffnen</a>` : ''}
      </div>`
    : '';
  body.innerHTML = linkBar + maptableShell(
    kind === 'payment' ? 'Vario-Zahlungsart' : 'Vario-Versandart',
    kind === 'payment' ? 'Gambio-Zahlart' : 'Gambio-Versandart',
    rows,
    false,
    kind === 'payment' ? 'Vario-Zahlungsbedingung' : 'Vario-Versandbedingung',
  );
  body.querySelectorAll<HTMLButtonElement>('[data-save]').forEach((btn) => {
    btn.onclick = () => {
      const tr = btn.closest('tr')!; const mod = tr.getAttribute('data-mod')!; const label = tr.getAttribute('data-label') || null;
      const mSel = tr.querySelector('[data-f="m"]') as HTMLSelectElement; const tSel = tr.querySelector('[data-f="t"]') as HTMLSelectElement;
      const methodId = mSel.value || null; const methodLabel = methodId ? (mSel.options[mSel.selectedIndex]?.text ?? null) : null;
      const termId = tSel.value || null; const termLabel = termId ? (tSel.options[tSel.selectedIndex]?.text ?? null) : null;
      void setChannelMapping(sctx, DEFAULT_SHOP, kind, mod, label, { methodId, methodLabel, termId, termLabel })
        .then(() => { showToast('Zuordnung gespeichert', 'success'); void rerender(body.closest('.go-settings') as HTMLElement); })
        .catch((e) => zeigeFehler('Aktion fehlgeschlagen', e));
    };
  });
}

/** Wie bei den Ländern: Sperre gegen einen neuen Worker-Job bei jedem Abschnittswechsel. */
let carrierAutoSyncAt = 0;
const CARRIER_AUTO_SYNC_MIN_GAP_MS = 5 * 60_000;

// Paketdienste: listet die Vario-Carrier (kind='carrier') und lässt je Carrier den Gambio-
// Paketdienst für den Tracking-Link wählen (feeds gambio.push_tracking). Carrier kommen aus zwei
// Quellen: dem Worker-Job gambio.sync_carriers (holt die volle Liste via computed-query) und
// passiv per Discovery, sobald ein Versand verarbeitet wurde. Der Abgleich läuft beim Öffnen des
// Abschnitts automatisch — der Anwender soll die Liste vorfinden, nicht erst anfordern müssen.
async function viewCarrierMapping(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const [rows, parcelOpts, creds] = await Promise.all([
    loadChannelMappings(sctx, DEFAULT_SHOP, 'carrier').catch(() => []),
    loadParcelServiceOptions(sctx, DEFAULT_SHOP).catch(() => ({ parcelServices: [] as Array<{ id: string; name: string }> })),
    // Best-effort, nur fuer den Deeplink ins Shop-Backend.
    loadShopPublicCreds(sctx, DEFAULT_SHOP).catch(() => null),
  ]);
  const { parcelServices } = parcelOpts;
  const sub = document.getElementById('go-psub');
  const done = rows.filter((r) => r.gambioParcelServiceId != null).length;
  if (sub) sub.textContent = `${done}/${rows.length} zugeordnet`;
  setOpenCount(body.closest('.go-settings') as HTMLElement, 'paketdienste', rows.length - done);

  const autoDue = Date.now() - carrierAutoSyncAt > CARRIER_AUTO_SYNC_MIN_GAP_MS;
  // Direktlinks in beide Verwaltungen: links kommen die Versanddienstleister her, rechts die
  // Versandmodule des Shops. `<a target="_blank">`, weil `window.open` im Vario-iframe (ohne
  // allow-popups) nichts tut. Fehlt eine Basis, entfaellt der jeweilige Link einzeln.
  const varioCarriers = varioCarrierAdminUrl();
  const shopShipping = gambioShippingModulesAdminUrl(creds?.base_url ?? null);
  const toolbar = `<div class="map-toolbar"><span class="map-stat"><b>${rows.length}</b> Carrier</span>
    ${varioCarriers ? `<a class="btn btn-ghost btn-sm" href="${escHtml(varioCarriers)}" target="_blank" rel="noopener">Versender in VARIO öffnen</a>` : ''}
    ${shopShipping ? `<a class="btn btn-ghost btn-sm" href="${escHtml(shopShipping)}" target="_blank" rel="noopener">Versandmodule in Gambio öffnen</a>` : ''}
    <span class="hint" style="margin-left:auto;">${autoDue ? 'Carrier werden geladen …' : 'Aus VARIO geladen'}</span>
    <button class="btn btn-ghost btn-sm" id="go-carrier-sync">Aktualisieren</button></div>`;

  /** „Aktualisieren" hebt die Sperre auf und lädt den Abschnitt neu — dabei läuft der
   *  Carrier-Abgleich erneut. Ein eigener Knopf nur fürs Anfordern der Liste entfällt. */
  function wireCarrierSync(): void {
    const btn = body.querySelector<HTMLButtonElement>('#go-carrier-sync');
    if (!btn) return;
    btn.onclick = () => {
      btn.disabled = true;
      carrierAutoSyncAt = 0;
      void rerender(body.closest('.go-settings') as HTMLElement);
    };
  }

  /** Abgleich beim Öffnen: anstossen, abwarten, Abschnitt neu zeichnen. Der erneute Aufruf
   *  dieser Funktion läuft dann in die Sperre und stoesst nichts Neues an — keine Schleife. */
  function autoSyncCarriers(): void {
    if (!autoDue) return;
    carrierAutoSyncAt = Date.now();
    void (async () => {
      try {
        const jobId = await enqueueCarrierSync(sctx, DEFAULT_SHOP);
        if (jobId) await awaitJob(sctx.supabase, jobId, 90_000);
      } catch (err) {
        // Fehlschlag ist kein Grund, die vorhandene Liste zu verwerfen; beim nächsten Öffnen
        // wird es erneut versucht.
        carrierAutoSyncAt = 0;
        zeigeFehler('Carrier-Abgleich fehlgeschlagen', err);
        return;
      }
      await rerender(body.closest('.go-settings') as HTMLElement);
    })();
  }

  if (rows.length === 0) {
    body.innerHTML = toolbar + `<div class="hint">Noch keine Versanddienstleister geladen. Der Abgleich mit VARIO läuft gerade; „Aktualisieren" wiederholt ihn. Carrier erscheinen auch automatisch, sobald ein Versand mit ihnen verarbeitet wurde.</div>`;
    wireCarrierSync();
    autoSyncCarriers();
    return;
  }

  function buildParcelOpts(selectedId: string | null): string {
    const empty = `<option value="">— bitte wählen —</option>`;
    return empty + parcelServices.map((p) => `<option value="${escHtml(p.id)}"${p.id === selectedId ? ' selected' : ''}>${escHtml(p.name)}</option>`).join('');
  }

  const rowsHtml = rows.map((r) => {
    const selectedId = r.gambioParcelServiceId ?? null;
    const isDone = selectedId != null;
    return `<tr data-mod="${escHtml(r.gambioModule)}" data-label="${escHtml(r.gambioLabel ?? '')}">
      <td><div class="v-cell"><span class="vn">${escHtml(r.gambioLabel ?? r.gambioModule)}</span><span class="vid mono">${escHtml(r.gambioModule)}</span></div></td>
      ${arrowCell()}
      <td class="g-cell"><div class="selwrap"><select class="sel" data-f="parcel">${buildParcelOpts(selectedId)}</select></div></td>
      <td class="st-cell">${chip(isDone)}</td>
    </tr>`;
  }).join('');

  body.innerHTML = toolbar + maptableShell('Vario-Carrier (Versand)', 'Gambio-Paketdienst', rowsHtml);
  wireCarrierSync();
  autoSyncCarriers();

  // Auto-Save bei Auswahl (wie Produkttyp): kein Speichern-Button je Zeile.
  body.querySelectorAll<HTMLSelectElement>('[data-f="parcel"]').forEach((sel) => {
    sel.onchange = () => {
      const tr = sel.closest('tr')!;
      const mod = tr.getAttribute('data-mod')!;
      const label = tr.getAttribute('data-label') || null;
      const parcelId = sel.value || null;
      const parcelLabel = parcelId ? (sel.options[sel.selectedIndex]?.text ?? null) : null;
      sel.disabled = true;
      void setChannelMapping(sctx, DEFAULT_SHOP, 'carrier', mod, label, {
        methodId: null, methodLabel: null, termId: null, termLabel: null,
        parcelServiceId: parcelId, parcelServiceLabel: parcelLabel,
      })
        .then(() => { showToast('Zuordnung gespeichert', 'success'); void rerender(body.closest('.go-settings') as HTMLElement); })
        .catch((err: Error) => { zeigeFehler('Aktion fehlgeschlagen', err); sel.disabled = false; });
    };
  });
}

async function viewTaxClassMapping(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const [opts, mappings, creds] = await Promise.all([
    loadTaxClassOptions(sctx, DEFAULT_SHOP).catch(() => ({ gambioTaxClasses: [], varioTaxSchemas: [] })),
    loadChannelMappings(sctx, DEFAULT_SHOP, 'tax_class').catch(() => []),
    loadShopPublicCreds(sctx, DEFAULT_SHOP).catch(() => null),
  ]);

  // Direktlinks in beide Verwaltungen — wie bei den Zahlungsarten. Anlass (Betreiber, 25.08.2026):
  // für die Gambio-Steuerklasse „elektronisch erbrachte Leistung“ gibt es in VARIO keinen
  // passenden Steuersatz. Genau dann braucht man beide Seiten, und zwar ohne sie zu suchen.
  const taxLinkBar = (): string => {
    const varioAdmin = varioTaxesAdminUrl();
    const shopAdmin = gambioTaxRatesAdminUrl(creds?.base_url ?? null);
    if (!varioAdmin && !shopAdmin) return '';
    return `<div class="map-toolbar">
      ${varioAdmin ? `<a class="btn btn-ghost btn-sm" href="${escHtml(varioAdmin)}" target="_blank" rel="noopener">Steuersätze in VARIO öffnen</a>` : ''}
      ${shopAdmin ? `<a class="btn btn-ghost btn-sm" href="${escHtml(shopAdmin)}" target="_blank" rel="noopener">Steuersätze in Gambio öffnen</a>` : ''}
    </div>`;
  };
  const { gambioTaxClasses, varioTaxSchemas } = opts;
  const sub = document.getElementById('go-psub');
  const done = gambioTaxClasses.filter((tc) => mappings.some((m) => m.gambioModule === tc.id && m.varioMethodId != null)).length;
  if (sub) sub.textContent = `${done}/${gambioTaxClasses.length} zugeordnet`;
  setOpenCount(body.closest('.go-settings') as HTMLElement, 'steuerklassen', gambioTaxClasses.length - done);

  if (gambioTaxClasses.length === 0) {
    body.innerHTML = taxLinkBar() + `<div class="hint">Keine Gambio-Steuerklassen gefunden (oder Liste nicht abrufbar).</div>`;
    return;
  }

  const mapByTaxClassId = new Map(mappings.map((m) => [m.gambioModule, m]));

  function buildSchemaOpts(selectedId: string | null): string {
    const emptyOpt = `<option value="">— bitte wählen —</option>`;
    const opts = varioTaxSchemas.map((s) =>
      `<option value="${escHtml(s.id)}"${s.id === selectedId ? ' selected' : ''}>${escHtml(s.label)}</option>`
    );
    return emptyOpt + opts.join('');
  }

  const rows = gambioTaxClasses.map((tc) => {
    const cur = mapByTaxClassId.get(tc.id);
    const selectedSchemaId = cur?.varioMethodId ?? null;
    const isDone = selectedSchemaId != null;
    return `<tr data-tcid="${escHtml(tc.id)}" data-tclabel="${escHtml(tc.title)}">
      <td>
        <div class="selwrap"><select class="sel" data-f="schema">${buildSchemaOpts(selectedSchemaId)}</select></div>
      </td>
      ${arrowCell()}
      <td class="g-cell">
        <div class="v-cell"><span class="vn">${escHtml(tc.title)}</span><span class="vid mono">${escHtml(tc.id)}</span></div>
      </td>
      <td class="st-cell">${chip(isDone)}</td>
    </tr>`;
  }).join('');

  body.innerHTML = taxLinkBar() + maptableShell('Vario-Steuerschema', 'Gambio-Steuerklasse', rows);

  // Auto-Save bei Auswahl (wie Produkttyp): kein Speichern-Button je Zeile.
  body.querySelectorAll<HTMLSelectElement>('[data-f="schema"]').forEach((schemaSel) => {
    schemaSel.onchange = () => {
      const tr = schemaSel.closest('tr')!;
      const taxClassId = tr.getAttribute('data-tcid')!;
      const taxClassTitle = tr.getAttribute('data-tclabel') || null;
      const schemaId = schemaSel.value || null;
      const schemaLabel = schemaId ? (schemaSel.options[schemaSel.selectedIndex]?.text ?? null) : null;
      schemaSel.disabled = true;
      void setChannelMapping(sctx, DEFAULT_SHOP, 'tax_class', taxClassId, taxClassTitle, {
        methodId: schemaId,
        methodLabel: schemaLabel,
        termId: null,
        termLabel: null,
      })
        .then(() => {
          showToast('Zuordnung gespeichert', 'success');
          void rerender(body.closest('.go-settings') as HTMLElement);
        })
        .catch((e: Error) => {
          zeigeFehler('Aktion fehlgeschlagen', e);
          schemaSel.disabled = false;
        });
    };
  });
}

async function viewHerstellerMapping(body: HTMLElement, sctx: SettingsCtx, shopKey: string): Promise<void> {
  const [opts, mappings] = await Promise.all([
    loadManufacturerOptions(sctx, shopKey).catch(() => ({ gambioManufacturers: [], varioManufacturers: [], suggestions: {} as Record<string, string> })),
    getManufacturerMappings(sctx, shopKey).catch(() => []),
  ]);
  const { gambioManufacturers, varioManufacturers, suggestions } = opts;
  const sub = document.getElementById('go-psub');
  const mapByGambioId = new Map(mappings.map((m) => [m.gambio_manufacturer_id, m]));
  const done = gambioManufacturers.filter((m) => (mapByGambioId.get(m.id)?.vario_account_id ?? null) != null).length;
  if (sub) sub.textContent = `${done}/${gambioManufacturers.length} zugeordnet`;
  setOpenCount(body.closest('.go-settings') as HTMLElement, 'hersteller', gambioManufacturers.length - done);

  if (gambioManufacturers.length === 0) {
    body.innerHTML = `<div class="hint">Keine Gambio-Hersteller gefunden (oder Liste nicht abrufbar).</div>`;
    return;
  }

  function buildAccountOpts(selectedId: string | null): string {
    const emptyOpt = `<option value="">— nicht zugeordnet —</option>`;
    const opts = varioManufacturers.map((a) =>
      `<option value="${escHtml(a.accountId)}"${a.accountId === selectedId ? ' selected' : ''}>${escHtml(a.label)}</option>`
    );
    return emptyOpt + opts.join('');
  }

  const rows = gambioManufacturers.map((gm) => {
    const cur = mapByGambioId.get(gm.id);
    const savedId = cur?.vario_account_id ?? null;
    const suggestedId = suggestions[String(gm.id)] ?? null;
    const selectedId = savedId ?? suggestedId;
    const isDone = savedId != null;
    const isSuggestionOnly = !isDone && suggestedId != null;
    return `<tr data-gmid="${escHtml(String(gm.id))}" data-gmname="${escHtml(gm.name)}">
      <td>
        <div class="selwrap"><select class="sel" data-f="account">${buildAccountOpts(selectedId)}</select></div>
        ${isSuggestionOnly ? '<div class="hint" style="margin-top:4px;">Vorschlag - noch nicht gespeichert</div>' : ''}
      </td>
      ${arrowCell()}
      <td class="g-cell">
        <div class="v-cell"><span class="vn">${escHtml(gm.name)}</span><span class="vid mono">#${escHtml(String(gm.id))}</span></div>
      </td>
      <td class="st-cell" style="width:auto;white-space:nowrap;"><div style="display:flex;align-items:center;gap:10px;">${chip(isDone)}<button class="btn btn-primary btn-sm" data-save>Speichern</button></div></td>
    </tr>`;
  }).join('');

  body.innerHTML = maptableShell('Vario-Hersteller-Account', 'Gambio-Hersteller', rows);

  body.querySelectorAll<HTMLButtonElement>('[data-save]').forEach((btn) => {
    btn.onclick = () => {
      const tr = btn.closest('tr')!;
      const gambioId = Number(tr.getAttribute('data-gmid'));
      const gambioName = tr.getAttribute('data-gmname') || null;
      const accountSel = tr.querySelector<HTMLSelectElement>('[data-f="account"]')!;
      const accountId = accountSel.value || null;
      const accountLabel = accountId ? (accountSel.options[accountSel.selectedIndex]?.text ?? null) : null;
      btn.disabled = true;
      const op = accountId
        ? setManufacturerMapping(sctx, shopKey, gambioId, gambioName, accountId, accountLabel)
        : deleteManufacturerMapping(sctx, shopKey, gambioId);
      void op
        .then(() => {
          showToast('Zuordnung gespeichert', 'success');
          void rerender(body.closest('.go-settings') as HTMLElement);
        })
        .catch((e: Error) => {
          zeigeFehler('Aktion fehlgeschlagen', e);
          btn.disabled = false;
        });
    };
  });
}

// Produkttyp + Lieferstatus: Vario-Seite ist der feste Treiber (Enum), Gambio-Seite ist
// ebenfalls ein fester Katalog (keine REST-Katalog-API) -> rechts editierbares Select statt Label.
async function viewProductFieldMapping(
  body: HTMLElement, sctx: SettingsCtx, shopKey: string,
  kind: ProductFieldMappingKind,
  varioCatalog: ReadonlyArray<{ value: string; label: string }>,
  gambioCatalog: ReadonlyArray<{ value: string; label: string }>,
  varioHead: string, gambioHead: string,
): Promise<void> {
  const mappings = await getProductFieldMappings(sctx, shopKey, kind).catch(() => []);
  const mapByVarioValue = new Map(mappings.map((m) => [m.vario_value, m]));
  const sub = document.getElementById('go-psub');
  const done = varioCatalog.filter((v) => (mapByVarioValue.get(v.value)?.gambio_value ?? null) != null).length;
  if (sub) sub.textContent = `${done}/${varioCatalog.length} zugeordnet`;
  setOpenCount(body.closest('.go-settings') as HTMLElement, kind === 'product_type' ? 'produkttyp' : 'lieferstatus', varioCatalog.length - done);

  function buildGambioOpts(selectedValue: string | null): string {
    const emptyOpt = `<option value="">— nicht zugeordnet —</option>`;
    const opts = gambioCatalog.map((g) =>
      `<option value="${escHtml(g.value)}"${g.value === selectedValue ? ' selected' : ''}>${escHtml(g.label)}</option>`
    );
    // Zugeordneter Wert nicht (mehr) im Katalog (z.B. Lieferstatus gelöscht) → sichtbar halten statt still verschieben.
    if (selectedValue && !gambioCatalog.some((g) => g.value === selectedValue)) {
      opts.push(`<option value="${escHtml(selectedValue)}" selected>ID ${escHtml(selectedValue)} (nicht im Katalog)</option>`);
    }
    return emptyOpt + opts.join('');
  }

  const rows = varioCatalog.map((v) => {
    const cur = mapByVarioValue.get(v.value);
    const selectedValue = cur?.gambio_value ?? null;
    const isDone = selectedValue != null;
    return `<tr data-vvalue="${escHtml(v.value)}" data-vlabel="${escHtml(v.label)}">
      <td>
        <div class="v-cell"><span class="vn">${escHtml(v.label)}</span><span class="vid mono">${escHtml(v.value)}</span></div>
      </td>
      ${arrowCell()}
      <td class="g-cell">
        <div class="selwrap"><select class="sel" data-f="gvalue">${buildGambioOpts(selectedValue)}</select></div>
      </td>
      <td class="st-cell">${chip(isDone)}</td>
    </tr>`;
  }).join('');

  body.innerHTML = maptableShell(varioHead, gambioHead, rows);

  body.querySelectorAll<HTMLSelectElement>('[data-f="gvalue"]').forEach((sel) => {
    sel.onchange = () => {
      const tr = sel.closest('tr')!;
      const varioValue = tr.getAttribute('data-vvalue')!;
      const varioLabel = tr.getAttribute('data-vlabel') || null;
      const gambioValue = sel.value || null;
      const gambioLabel = gambioValue ? (sel.options[sel.selectedIndex]?.text ?? null) : null;
      sel.disabled = true;
      const op = gambioValue
        ? setProductFieldMapping(sctx, shopKey, kind, varioValue, varioLabel, gambioValue, gambioLabel)
        : deleteProductFieldMapping(sctx, shopKey, kind, varioValue);
      void op
        .then(() => { showToast('Zuordnung gespeichert', 'success'); void rerender(body.closest('.go-settings') as HTMLElement); })
        .catch((e: Error) => { zeigeFehler('Aktion fehlgeschlagen', e); sel.disabled = false; });
    };
  });
}

async function viewProductTypeMapping(body: HTMLElement, sctx: SettingsCtx, shopKey: string): Promise<void> {
  await viewProductFieldMapping(body, sctx, shopKey, 'product_type', VARIO_PRODUCT_TYPES, GAMBIO_PRODUCT_TYPES, 'Vario-Artikelart', 'Gambio-Produkttyp');
}

async function viewShippingStatusMapping(body: HTMLElement, sctx: SettingsCtx, shopKey: string): Promise<void> {
  let catalog = await loadShippingStatusCatalog(sctx, shopKey).catch(() => [] as ShippingStatusCatalogEntry[]);
  // Erstmalig (nur Fallback-Werte, nichts in DB): Standard-Lieferstatus übernehmen, damit sie
  // editier- und löschbar sind und der Katalog fortan die einzige Quelle ist.
  if (catalog.length > 0 && catalog.every((e) => !e.persisted)) {
    await Promise.all(catalog.map((e) => saveShippingStatusCatalogEntry(sctx, shopKey, e.gambio_value, e.label, e.sort_order).catch(() => {})));
    catalog = await loadShippingStatusCatalog(sctx, shopKey).catch(() => catalog);
  }
  // Für die Warnung beim Löschen: welche Lieferstatus sind aktuell einer Bestandsstatus-Zuordnung zugewiesen?
  const mappings = await getProductFieldMappings(sctx, shopKey, 'shipping_status').catch(() => []);
  const usedValues = new Set(mappings.map((m) => m.gambio_value).filter((v): v is string => !!v));

  body.innerHTML = `
    <div class="actions" style="margin-top:0;margin-bottom:14px;"><button class="btn btn-primary" id="go-ss-save">Speichern</button></div>
    <div style="font-weight:600;margin:0 0 6px;">Gambio-Lieferstatus (lokaler Katalog)</div>
    <div class="hint" style="margin:0 0 12px;">Gambio bietet keinen API-Katalog für Lieferstatus. Lege die im Gambio-Backend vorhandenen Lieferstatus hier mit ihrer <strong>Gambio-ID</strong> (= <code>shippingTimeId</code>) und Bezeichnung an. Die ID muss mit dem Shop-Backend übereinstimmen; die Bezeichnung dient nur der Anzeige im Connector.</div>
    <div data-ss-catalog></div>
    <div style="font-weight:600;margin:24px 0 8px;">Zuordnung: Vario-Bestandsstatus → Gambio-Lieferstatus</div>
    <div data-ss-map></div>`;

  renderShippingCatalogEditor(
    body.querySelector('[data-ss-catalog]') as HTMLElement, sctx, shopKey, catalog, usedValues,
    body.querySelector<HTMLButtonElement>('#go-ss-save')!,
  );
  await viewProductFieldMapping(
    body.querySelector('[data-ss-map]') as HTMLElement,
    sctx, shopKey, 'shipping_status', VARIO_STOCK_STATES,
    catalog.map((e) => ({ value: e.gambio_value, label: e.label })),
    'Vario-Bestandsstatus', 'Gambio-Lieferstatus',
  );
}

// Editierbarer Lieferstatus-Katalog: Anlegen (ID + Bezeichnung), Umbenennen, zweistufiges Löschen.
// Kein Browser-Dialog. Reload über rerender der ganzen Settings-Seite.
//
// Zwei Knöpfe mit klar getrennter Aufgabe: `saveBtn` oben rechts schreibt die geänderten
// Bezeichnungen der VORHANDENEN Zeilen; „Neu anlegen“ unter der Liste legt einen weiteren
// Lieferstatus an. Die Anlage steht bewusst UNTER der Liste — erst sieht man, was es gibt, dann
// hängt man etwas an. Gelöscht wird je Zeile: Löschen ist keine Eingabe, die man sammelt, und
// hängt am zweistufigen Klick. Die Zuordnungstabelle darunter speichert jede Auswahl sofort.
function renderShippingCatalogEditor(
  host: HTMLElement, sctx: SettingsCtx, shopKey: string,
  catalog: ShippingStatusCatalogEntry[], usedValues: Set<string>,
  saveBtn: HTMLButtonElement,
): void {
  const reload = () => { void rerender(host.closest('.go-settings') as HTMLElement); };

  const createRow = `<div style="display:flex;gap:8px;align-items:center;margin:14px 0 0;flex-wrap:wrap;">
    <input class="sel" data-ss-new-id type="number" step="1" min="1" placeholder="Gambio-ID" style="max-width:120px;">
    <input class="sel" data-ss-new-label placeholder="Bezeichnung (z. B. ca. 3 Wochen)" style="max-width:280px;">
    <button class="btn btn-primary btn-sm" data-ss-create>Neu anlegen</button>
  </div>`;

  const rows = catalog.map((e) => `
    <tr data-ss-gid="${escHtml(e.gambio_value)}">
      <td class="mono" style="width:90px;white-space:nowrap;">${escHtml(e.gambio_value)}</td>
      <td><input class="sel" data-ss-label value="${escHtml(e.label)}" style="max-width:320px;"></td>
      <td class="st-cell" style="width:auto;white-space:nowrap;"><div style="display:flex;align-items:center;gap:8px;justify-content:flex-end;">
        <button class="btn btn-sm" data-ss-del>Löschen</button>
      </div></td>
    </tr>`).join('');

  host.innerHTML = (catalog.length === 0
    ? `<div class="hint">Noch keine Lieferstatus — unten einen anlegen.</div>`
    : `<table class="maptable"><thead><tr><th>Gambio-ID</th><th>Bezeichnung</th><th class="st-cell">Aktion</th></tr></thead><tbody>${rows}</tbody></table>`)
    + createRow;

  // Oben rechts: schreibt die geänderten Bezeichnungen der vorhandenen Zeilen. Nur wirklich
  // geänderte — unveränderte Zeilen erzeugen sonst bei jedem Klick sinnlose Schreibvorgänge.
  saveBtn.onclick = () => {
    const changed: Array<{ gid: string; label: string; sort: number }> = [];
    for (const tr of Array.from(host.querySelectorAll<HTMLTableRowElement>('tr[data-ss-gid]'))) {
      const gid = tr.getAttribute('data-ss-gid')!;
      const label = tr.querySelector<HTMLInputElement>('[data-ss-label]')!.value.trim();
      const cur = catalog.find((e) => e.gambio_value === gid);
      if (!label) { showToast(`Bezeichnung fehlt bei ID ${gid}`, 'error'); return; }
      if (cur && label === cur.label) continue;
      changed.push({ gid, label, sort: cur?.sort_order ?? 0 });
    }
    if (changed.length === 0) { showToast('Keine Änderungen zu speichern.', 'info'); return; }

    saveBtn.disabled = true;
    void (async () => {
      try {
        for (const c of changed) {
          await saveShippingStatusCatalogEntry(sctx, shopKey, c.gid, c.label, c.sort);
        }
        showToast(`${changed.length} Bezeichnung${changed.length === 1 ? '' : 'en'} gespeichert`, 'success');
        reload();
      } catch (err) {
        zeigeFehler('Aktion fehlgeschlagen', err);
        saveBtn.disabled = false;
      }
    })();
  };

  // Unter der Liste: legt einen weiteren Lieferstatus an.
  const createBtn = host.querySelector<HTMLButtonElement>('[data-ss-create]')!;
  createBtn.onclick = () => {
    const idInput = host.querySelector<HTMLInputElement>('[data-ss-new-id]')!;
    const labelInput = host.querySelector<HTMLInputElement>('[data-ss-new-label]')!;
    const newId = idInput.value.trim();
    const newLabel = labelInput.value.trim();
    if (!/^\d+$/.test(newId)) { showToast('Bitte eine numerische Gambio-ID eingeben', 'error'); return; }
    if (!newLabel) { showToast('Bitte eine Bezeichnung eingeben', 'error'); return; }
    if (catalog.some((e) => e.gambio_value === newId)) { showToast(`ID ${newId} existiert bereits`, 'error'); return; }

    createBtn.disabled = true;
    const nextSort = catalog.reduce((m, e) => Math.max(m, e.sort_order), -1) + 1;
    void saveShippingStatusCatalogEntry(sctx, shopKey, newId, newLabel, nextSort)
      .then(() => { showToast('Lieferstatus angelegt', 'success'); reload(); })
      .catch((err: Error) => { zeigeFehler('Aktion fehlgeschlagen', err); createBtn.disabled = false; });
  };

  host.querySelectorAll<HTMLButtonElement>('[data-ss-del]').forEach((btn) => {
    let armed = false; let t: ReturnType<typeof setTimeout> | null = null;
    const tr = btn.closest('tr')!;
    const gid = tr.getAttribute('data-ss-gid')!;
    const inUse = usedValues.has(gid);
    btn.onclick = () => {
      if (!armed) {
        armed = true;
        btn.textContent = inUse ? 'In Zuordnung genutzt – trotzdem löschen?' : 'Wirklich löschen?';
        t = setTimeout(() => { armed = false; btn.textContent = 'Löschen'; }, 3500);
        return;
      }
      if (t) clearTimeout(t);
      btn.disabled = true;
      void deleteShippingStatusCatalogEntry(sctx, shopKey, gid)
        .then(() => { showToast('Lieferstatus gelöscht', 'success'); reload(); })
        .catch((err: Error) => { zeigeFehler('Aktion fehlgeschlagen', err); btn.disabled = false; armed = false; btn.textContent = 'Löschen'; });
    };
  });
}

async function viewBasePriceUnitMapping(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const data = await loadBasePriceUnits(sctx, DEFAULT_SHOP).catch(() => null);
  if (!data || (data.gambioUnits.length === 0 && data.gambioQuantityUnits.length === 0)) {
    body.innerHTML = `<div class="hint">Keine Gambio-Einheiten gefunden (oder Liste nicht abrufbar).</div>`;
    return;
  }
  const { gambioUnits, varioUnits, mappings, suggestions, gambioQuantityUnits, quantityMappings, quantitySuggestions } = data;

  // Beide Achsen als normalisierte Zeilen: Grundpreis-VPE (/vpe) UND Mengeneinheit (/quantity_units).
  const vpeMap = new Map(mappings.map((m) => [m.gambio_vpe_id, m.vario_unit_abbr]));
  const vpeSugg = new Map(suggestions.map((s) => [s.vpeId, s.varioAbbr]));
  const quMap = new Map(quantityMappings.map((m) => [m.gambio_quantity_unit_id, m.vario_unit_abbr]));
  const quSugg = new Map(quantitySuggestions.map((s) => [s.quantityUnitId, s.varioAbbr]));

  const sub = document.getElementById('go-psub');
  const doneVpe = gambioUnits.filter((u) => (vpeMap.get(u.id) ?? null) != null).length;
  const doneQu = gambioQuantityUnits.filter((u) => (quMap.get(u.id) ?? null) != null).length;
  if (sub) sub.textContent = `${doneVpe}/${gambioUnits.length} Grundpreis · ${doneQu}/${gambioQuantityUnits.length} Mengeneinheit`;

  // Die Option traegt die Vario-Einheiten-Id als `data-unit-id` mit. Der Wert bleibt die
  // ABKUERZUNG, weil die Vorauswahl und der Weg VARIO -> Gambio daran haengen; die Id kommt
  // zusaetzlich, damit der Weg Gambio -> VARIO sie speichern kann (Migration 0356) und dort nicht
  // je Artikel den ganzen Einheitenkatalog laden muss.
  function buildUnitOpts(selected: string | null, emptyLabel: string): string {
    const empty = `<option value="">${escHtml(emptyLabel)}</option>`;
    const opts = varioUnits.map((u) =>
      `<option value="${escHtml(u.abbr)}" data-unit-id="${escHtml(u.id)}"`
      + `${u.abbr === selected ? ' selected' : ''}>${escHtml(u.label)} (${escHtml(u.abbr)})</option>`
    );
    return empty + opts.join('');
  }

  /**
   * Eine Gruppe (Grundpreis oder Mengeneinheit) mit EINEM Speichern-Knopf oben rechts — wie bei den
   * Varianten. Der Knopf schreibt alle geänderten Zeilen der Gruppe: umbenannter Gambio-Name und/oder
   * geänderte Vario-Zuordnung. Zeilen-Speichern-Knöpfe gibt es nicht mehr.
   *
   * Je Zeile bleiben ZWEI Aktionen, und sie sind absichtlich verschieden benannt, weil sie
   * Verschiedenes tun: „Zuordnung entfernen“ löst nur die Verbindung zur Vario-Einheit,
   * „In Gambio löschen“ löscht die Einheit im Shop. Beides in einen Knopf zu legen wäre
   * gefährlich — der Shop-seitige Löschvorgang ist nicht rückholbar.
   *
   * Die Anlage steht UNTER der Liste: erst sieht man, was es gibt, dann hängt man etwas an.
   */
  function section(kind: 'vpe' | 'qu', title: string, units: Array<{ id: number; name: string }>, selForId: (id: number) => EffectiveUnit, gHead: string, emptyLabel: string): string {
    const kindLabel = kind === 'vpe' ? 'Grundpreis-Einheit' : 'Mengeneinheit';
    const kindPlural = kind === 'vpe' ? 'Grundpreis-Einheiten' : 'Mengeneinheiten';
    const placeholder = kind === 'vpe' ? 'z. B. 100 ml' : 'z. B. Karton';

    const head = `<div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin:0 0 8px;">
      <div style="font-weight:600;">${escHtml(title)}</div>
      <button class="btn btn-primary btn-sm" data-unit-save="${kind}">Speichern</button>
    </div>
    <div class="hint" style="margin:0 0 10px;">Neue Einheiten werden <strong>im Gambio-Shop</strong> angelegt. Vario-Einheiten werden hier nur zugeordnet, nicht erzeugt.</div>`;

    // Anlage-Zeile unter der Liste.
    const createRow = `<div data-create-box="${kind}" style="display:flex;gap:8px;align-items:center;margin:12px 0 0;flex-wrap:wrap;">
      <span style="font-size:12px;color:#6b7280;white-space:nowrap;">Neue Gambio-${escHtml(kindLabel)}:</span>
      <input class="sel" data-create-name placeholder="${placeholder}" style="max-width:200px;">
      <button class="btn btn-primary btn-sm" data-create-kind="${kind}">Neu anlegen</button>
    </div>`;

    if (units.length === 0) {
      return `${head}<div class="hint">Noch keine Gambio-${kindPlural} — unten eine in Gambio anlegen.</div>${createRow}`;
    }

    const rows = units.map((u) => {
      const sel = selForId(u.id);
      // `data-orig-unit` = der beim Zeichnen gewählte Wert. Der Gruppen-Knopf schreibt nur Zeilen,
      // die davon abweichen — ein bloßer Vorschlag, den niemand angefasst hat, wird dadurch NICHT
      // ungefragt festgeschrieben (dasselbe Verhalten wie vorher mit dem Zeilen-Knopf).
      const cur = sel.value ?? '';
      return `<tr data-kind="${kind}" data-gid="${u.id}" data-gname="${escHtml(u.name)}" data-orig-unit="${escHtml(cur)}">
        <td><div class="selwrap"><select class="sel" data-f="unit">${buildUnitOpts(sel.value, emptyLabel)}</select></div></td>
        ${arrowCell()}
        <td class="g-cell"><div style="display:flex;align-items:center;gap:8px;"><input class="sel" data-f="gname" value="${escHtml(u.name)}" style="max-width:180px;"></div></td>
        <td class="st-cell" style="width:auto;white-space:nowrap;"><div style="display:flex;align-items:center;gap:8px;justify-content:flex-end;">${unitChip(sel)}
          ${sel.saved ? `<button class="btn btn-ghost btn-sm" data-clear>Zuordnung entfernen</button>` : ''}
          <button class="btn btn-sm" data-del>In Gambio löschen</button></div></td>
      </tr>`;
    }).join('');
    return head + maptableShell('Vario-Einheit', gHead, rows) + createRow;
  }

  body.innerHTML = `
    ${section('vpe', 'Grundpreis-Einheit (VPE)', gambioUnits, (id) => effectiveUnit(id, vpeMap, vpeSugg), 'Gambio-Grundpreis-Einheit', '— kein Grundpreis —')}
    <div style="margin-top:26px;"></div>
    ${section('qu', 'Mengeneinheit (Verkaufseinheit)', gambioQuantityUnits, (id) => effectiveUnit(id, quMap, quSugg), 'Gambio-Mengeneinheit', '— keine Mengeneinheit —')}`;

  const reload = () => { void rerender(body.closest('.go-settings') as HTMLElement); };

  /** Schreibt eine Zeile: erst den geänderten Gambio-Namen (CRUD), dann die Vario-Zuordnung. */
  const writeRow = async (tr: HTMLElement, abbrOverride?: string | null): Promise<void> => {
    const kind = tr.getAttribute('data-kind') as 'vpe' | 'qu';
    const gid = Number(tr.getAttribute('data-gid'));
    const origName = tr.getAttribute('data-gname') || '';
    const newName = tr.querySelector<HTMLInputElement>('[data-f="gname"]')!.value.trim();
    const sel = tr.querySelector<HTMLSelectElement>('[data-f="unit"]')!;
    const abbr = abbrOverride !== undefined
      ? abbrOverride
      : (sel.value || null);
    // Id der gewaehlten Option. Beim Entfernen einer Zuordnung (abbrOverride === null) MUSS sie
    // ebenfalls leer sein, sonst bliebe eine Id ohne Abkuerzung stehen.
    const unitId = abbr === null
      ? null
      : (sel.selectedOptions[0]?.getAttribute('data-unit-id') || null);
    if (newName && newName !== origName) {
      await unitCatalogAdmin(sctx, DEFAULT_SHOP, { action: 'rename', catalog: kind === 'qu' ? 'quantity' : 'vpe', id: gid, nameDe: newName });
    }
    const label = newName || origName || null;
    if (kind === 'qu') await setQuantityUnitMapping(sctx, DEFAULT_SHOP, gid, label, abbr, unitId);
    else await setBasePriceUnitMapping(sctx, DEFAULT_SHOP, gid, label, abbr);
  };

  // EIN Speichern je Gruppe: schreibt alle Zeilen, an denen etwas geändert wurde.
  body.querySelectorAll<HTMLButtonElement>('[data-unit-save]').forEach((btn) => {
    btn.onclick = () => {
      const kind = btn.getAttribute('data-unit-save')!;
      const dirty = Array.from(body.querySelectorAll<HTMLTableRowElement>(`tr[data-kind="${kind}"]`)).filter((tr) => {
        const nameChanged = tr.querySelector<HTMLInputElement>('[data-f="gname"]')!.value.trim() !== (tr.getAttribute('data-gname') || '');
        const unitChanged = (tr.querySelector<HTMLSelectElement>('[data-f="unit"]')!.value || '') !== (tr.getAttribute('data-orig-unit') || '');
        return nameChanged || unitChanged;
      });
      if (dirty.length === 0) { showToast('Keine Änderungen zu speichern.', 'info'); return; }
      btn.disabled = true;
      void (async () => {
        try {
          for (const tr of dirty) await writeRow(tr);
          showToast(`${dirty.length} Zeile${dirty.length === 1 ? '' : 'n'} gespeichert`, 'success');
          reload();
        } catch (e) {
          zeigeFehler('Aktion fehlgeschlagen', e);
          btn.disabled = false;
        }
      })();
    };
  });

  // Zuordnung entfernen: löst NUR die Verbindung zur Vario-Einheit, rührt den Gambio-Katalog nicht an.
  body.querySelectorAll<HTMLButtonElement>('[data-clear]').forEach((btn) => {
    btn.onclick = () => {
      const tr = btn.closest('tr')! as HTMLElement;
      btn.disabled = true;
      void writeRow(tr, null)
        .then(() => { showToast('Zuordnung entfernt', 'success'); reload(); })
        .catch((e: Error) => { zeigeFehler('Aktion fehlgeschlagen', e); btn.disabled = false; });
    };
  });

  // In Gambio löschen: zweistufig (kein Browser-Dialog) — erster Klick armt, zweiter löscht.
  body.querySelectorAll<HTMLButtonElement>('[data-del]').forEach((btn) => {
    let armed = false; let t: ReturnType<typeof setTimeout> | null = null;
    btn.onclick = () => {
      const tr = btn.closest('tr')!;
      const kind = tr.getAttribute('data-kind') as 'vpe' | 'qu';
      const gid = Number(tr.getAttribute('data-gid'));
      if (!armed) {
        armed = true; btn.textContent = 'Wirklich in Gambio löschen?';
        t = setTimeout(() => { armed = false; btn.textContent = 'In Gambio löschen'; }, 3500);
        return;
      }
      if (t) clearTimeout(t);
      btn.disabled = true;
      void unitCatalogAdmin(sctx, DEFAULT_SHOP, { action: 'delete', catalog: kind === 'qu' ? 'quantity' : 'vpe', id: gid })
        .then(() => { showToast('In Gambio gelöscht', 'success'); reload(); })
        .catch((e: Error) => { zeigeFehler('Aktion fehlgeschlagen', e); btn.disabled = false; armed = false; btn.textContent = 'In Gambio löschen'; });
    };
  });

  // Anlegen: neue Gambio-Einheit im jeweiligen Katalog.
  body.querySelectorAll<HTMLButtonElement>('[data-create-kind]').forEach((btn) => {
    btn.onclick = () => {
      const kind = btn.getAttribute('data-create-kind') as 'vpe' | 'qu';
      const wrap = (btn.closest('[data-create-box]') ?? btn.parentElement) as HTMLElement;
      const input = wrap.querySelector<HTMLInputElement>('[data-create-name]')!;
      const name = input.value.trim();
      if (!name) { showToast('Bitte einen Namen eingeben', 'error'); return; }
      btn.disabled = true;
      void unitCatalogAdmin(sctx, DEFAULT_SHOP, { action: 'create', catalog: kind === 'qu' ? 'quantity' : 'vpe', nameDe: name })
        .then(() => { showToast('In Gambio angelegt', 'success'); reload(); })
        .catch((e: Error) => { zeigeFehler('Aktion fehlgeschlagen', e); btn.disabled = false; });
    };
  });
}

const ORDER_STATUS_EVENTS: Array<{ key: string; label: string }> = [
  { key: 'accept',           label: 'Auftrag angenommen' },
  { key: 'delivery',         label: 'Lieferschein erstellt' },
  { key: 'publish',          label: 'Lieferschein versendet' },
  { key: 'invoice',          label: 'Rechnung erstellt' },
  { key: 'delivery_invoice', label: 'Lieferschein + Rechnung' },
  { key: 'shipment',         label: 'Paket versendet (Tracking)' },
];

async function viewOrderStatusMapping(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const [opts, mappings] = await Promise.all([
    loadOrderStatusOptions(sctx, DEFAULT_SHOP).catch(() => ({ orderStatuses: [] as Array<{ id: string; label: string }> })),
    loadChannelMappings(sctx, DEFAULT_SHOP, 'order_status').catch(() => []),
  ]);
  const { orderStatuses } = opts;
  const sub = document.getElementById('go-psub');
  const done = ORDER_STATUS_EVENTS.filter((e) => mappings.some((m) => m.gambioModule === e.key && m.varioMethodId != null)).length;
  if (sub) sub.textContent = `${done}/${ORDER_STATUS_EVENTS.length} zugeordnet`;
  setOpenCount(body.closest('.go-settings') as HTMLElement, 'orderstatus', ORDER_STATUS_EVENTS.length - done);

  if (orderStatuses.length === 0) {
    body.innerHTML = `<div class="hint">Keine Gambio-Bestellstatus gefunden (oder Liste nicht abrufbar). API-Zugang prüfen.</div>`;
    return;
  }
  const mapByEvent = new Map(mappings.map((m) => [m.gambioModule, m]));

  function buildStatusOpts(selectedId: string | null): string {
    const empty = `<option value="">— kein Push —</option>`;
    return empty + orderStatuses.map((s) => `<option value="${escHtml(s.id)}"${s.id === selectedId ? ' selected' : ''}>${escHtml(s.label)}</option>`).join('');
  }

  const rows = ORDER_STATUS_EVENTS.map((e) => {
    const cur = mapByEvent.get(e.key);
    const selectedStatusId = cur?.varioMethodId ?? null;
    const isDone = selectedStatusId != null;
    return `<tr data-evkey="${escHtml(e.key)}" data-evlabel="${escHtml(e.label)}">
      <td><div class="v-cell"><span class="vn">${escHtml(e.label)}</span><span class="vid mono">${escHtml(e.key)}</span></div></td>
      ${arrowCell()}
      <td class="g-cell"><div class="selwrap"><select class="sel" data-f="status">${buildStatusOpts(selectedStatusId)}</select></div></td>
      <td class="st-cell">${chip(isDone)}</td>
    </tr>`;
  }).join('');

  // Auto-Save bei Auswahl (wie Produkttyp/Lieferstatus): kein Speichern-Button, die Zuordnung
  // wird direkt beim Ändern des Dropdowns persistiert. „— kein Push —" (leer) hebt die Zuordnung auf.
  body.innerHTML = maptableShell('Vario-Ereignis', 'Gambio-Status', rows);

  body.querySelectorAll<HTMLSelectElement>('[data-f="status"]').forEach((sel) => {
    sel.onchange = () => {
      const tr = sel.closest('tr')!;
      const evKey = tr.getAttribute('data-evkey')!;
      const evLabel = tr.getAttribute('data-evlabel') || null;
      const statusId = sel.value || null;
      const statusLabel = statusId ? (sel.options[sel.selectedIndex]?.text ?? null) : null;
      sel.disabled = true;
      void setChannelMapping(sctx, DEFAULT_SHOP, 'order_status', evKey, evLabel, { methodId: statusId, methodLabel: statusLabel, termId: null, termLabel: null })
        .then(() => { showToast('Zuordnung gespeichert', 'success'); void rerender(body.closest('.go-settings') as HTMLElement); })
        .catch((err: Error) => { zeigeFehler('Aktion fehlgeschlagen', err); sel.disabled = false; });
    };
  });
}

// Währungen: Diff der aktiven Währungen VARIO vs. Gambio-Shop (kein Übersetzungs-Mapping,
// deshalb NICHT in MAPPING_SECTIONS - eigener Erklärtext in panelHeadingBadge).
async function viewWaehrungen(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  const { rows, gambioAdminBaseUrl } = await loadCurrencyDiff(sctx, DEFAULT_SHOP).catch(() => ({ rows: [] as CurrencyDiffRow[], gambioAdminBaseUrl: '' }));

  const sub = document.getElementById('go-psub');

  if (rows.length === 0) {
    if (sub) sub.textContent = '';
    body.innerHTML = `<div class="hint">Währungen konnten nicht geladen werden. API-Zugang prüfen.</div>`;
    return;
  }

  const openCount = rows.filter((r) => r.status === 'onlyGambio').length;
  if (sub) sub.textContent = `${openCount} zur Aktivierung offen`;

  // Verwaltungs-Links (fest, im Panelkopf) - bewusst NICHT je Währung.
  const varioUrl = varioCurrenciesUrl();
  const gambioUrl = gambioCurrencyAdminUrl(gambioAdminBaseUrl);
  const manageLinks = `<div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:14px;">
    ${varioUrl ? `<a class="btn btn-ghost btn-sm" href="${escHtml(varioUrl)}" target="_blank" rel="noopener">In VARIO verwalten ↗</a>` : ''}
    ${gambioUrl ? `<a class="btn btn-ghost btn-sm" href="${escHtml(gambioUrl)}" target="_blank" rel="noopener">In Gambio verwalten ↗</a>` : ''}
  </div>`;

  const rowsHtml = rows.map((r) => {
    // Nur „nur Gambio" bekommt die Aktivieren-Aktion; sonst keine Zeilen-Aktion mehr
    // (Verwaltung läuft über die festen Kopf-Links).
    const actionHtml = r.status === 'onlyGambio'
      ? `<div style="margin-top:10px;"><button class="btn btn-ghost btn-sm" data-activate="${escHtml(r.iso)}">In VARIO aktivieren</button></div>`
      : '';
    const statusChip = r.status === 'both'
      ? '<span class="chip ok"><span class="d"></span>beidseitig</span>'
      : r.status === 'onlyGambio'
        ? '<span class="chip miss"><span class="d"></span>nur Gambio</span>'
        : '<span class="chip miss"><span class="d"></span>nur VARIO</span>';
    return `<tr data-iso="${escHtml(r.iso)}">
      <td><div class="v-cell"><span class="vn">${escHtml(r.name)}</span><span class="vid mono">${escHtml(r.iso)}</span></div></td>
      ${arrowCell()}
      <td class="g-cell"><div class="v-cell"><span class="vn">${r.gambioActive ? escHtml(r.name) : '—'}</span></div></td>
      <td class="st-cell">${statusChip}${actionHtml}</td>
    </tr>`;
  }).join('');

  body.innerHTML = manageLinks + maptableShell('VARIO', 'Gambio', rowsHtml);

  body.querySelectorAll<HTMLButtonElement>('[data-activate]').forEach((btn) => {
    btn.onclick = () => {
      const iso = btn.getAttribute('data-activate')!;
      btn.disabled = true;
      void activateVarioCurrency(sctx, DEFAULT_SHOP, iso)
        .then(() => { showToast('In VARIO aktiviert', 'success'); void rerender(body.closest('.go-settings') as HTMLElement); })
        .catch((err: Error) => { zeigeFehler('Aktion fehlgeschlagen', err); btn.disabled = false; });
    };
  });
}

// Sektionen der Gruppe „Mapping Vario ↔ Gambio": generische Zuordnungstabellen
// (links Vario-Wert, rechts Gambio-Wert) - gemeinsamer Erklärtext an der Panel-Überschrift.
const MAPPING_SECTIONS: ReadonlySet<SectionId> = new Set([
  'kundengruppen', 'kategorien', 'laender', 'zahlungsarten', 'versandarten', 'paketdienste',
  'steuerklassen', 'orderstatus', 'hersteller', 'produkttyp', 'lieferstatus',
]);

// Kontextbezogene Erklärung je Einstellungs-Sektion (ⓘ an der Panel-Überschrift).
// Für generische Wert-Zuordnungen greift der MAPPING_SECTIONS-Fallback, sofern hier
// kein spezifischerer Text steht.
const SECTION_INFO: Partial<Record<SectionId, string>> = {
  api: 'Shop-URL und Zugangsdaten dieses Gambio-Verkaufskanals. Beim Speichern wird die Verbindung geprüft. Hier lässt sich auch ein weiterer Gambio-Shop anbinden.',
  lager: 'Bestandsverhalten und virtueller Lagerbestand für bestandslose Artikel (z. B. Dienstleistungen), damit diese im Shop verfügbar bleiben.',
  import: 'Verhalten beim Anlegen neuer Produkte im Shop und beim Import aus Gambio nach VARIO (Kategorie, Bilder, Zusatzfelder, Kennungen).',
  darstellung: 'Drei Angaben gelten immer: der Aktiv-Status neu zugewiesener Artikel, das automatische Deaktivieren bei Entfernung aus dem Kanal und der Standard-Lieferstatus neu angelegter Produkte. Die eingerückten Werte darunter (z. B. Sichtbarkeit, Anzeigeoptionen) sind Vorgaben für NEU angelegte Produkte; bestehende bleiben unverändert.',
  betrieb: 'Verhalten der automatischen Bestellübernahme und des Sync-Betriebs für diesen Verkaufskanal.',
  kundenexport: 'Steuert den Austausch von Kundendaten zwischen VARIO und Gambio: automatisch in Richtung Gambio (anlegen/aktualisieren, inklusive Kundengruppen- und Länder-Zuordnung) und auf Knopfdruck in beide Richtungen.',
  kundengruppen: 'Ordnet jede Gambio-Kundengruppe einer VARIO-Kundengruppe UND/ODER einer Preisgruppe zu. Diese Zuordnung steuert die Preisfindung: Der im Produkt-Preiseditor gesetzte Preis wird an genau die hier gewählte(n) VARIO-Referenz(en) geschrieben. Wichtig: Setzt du BEIDE, verknüpft VARIO sie UND, der Preis greift dann nur, wenn Kunden- UND Preisgruppe gemeinsam zutreffen. Für einen Preis, der allein über die Kundengruppe (oder allein über die Preisgruppe) greift, nur die eine Seite zuordnen.',
  kategorien: 'Stößt den Kategorie-Abgleich zwischen Gambio und VARIO an. Neue, umbenannte oder verschobene Shop-Kategorien werden zusätzlich stündlich automatisch nachgezogen.',
  varianten: 'Legt fest, welches VARIO-Variantenattribut welcher Gambio-Option entspricht, damit Varianten sauber ausgesteuert werden.',
  optionen: 'Optionsgruppen (z. B. Farbe, Größe) und ihre Werte direkt anlegen, umbenennen, sortieren und löschen, mehrsprachig, inkl. Artikelnummer, Preis, Gewicht und Bild je Wert.',
  laender: 'Automatisch abgeglichene Zuordnung Ländercode ↔ VARIO-Land. Der Abgleich läuft beim Bestellimport, lässt sich hier aber auch manuell anstoßen.',
  zahlungsarten: 'Ordnet jede Gambio-Zahlungsart einer VARIO-Zahlungsart zu (je Beleg, ohne Fallback). Nicht zugeordnete Arten werden protokolliert.',
  versandarten: 'Ordnet jede Gambio-Versandart einer VARIO-Versandart zu (je Beleg, ohne Fallback). Nicht zugeordnete Arten werden protokolliert.',
  paketdienste: 'Ordnet den in VARIO verwendeten Versanddienstleister dem passenden Gambio-Paketdienst zu. Grundlage für die automatische Trackingnummer-Übermittlung. „Vario-Carrier aktualisieren" lädt alle Dienstleister auf einmal.',
  steuerklassen: 'Ordnet die Artikel-Steuerklasse zwischen Shop und VARIO zu (beidseitig). Wirkt beim Artikelimport und bei der Artikelanlage.',
  orderstatus: 'Ordnet jedem Vorgang (Auftragsannahme, Lieferschein, Rechnung, Versand) einen Gambio-Bestellstatus zu, der nach dem Vorgang automatisch im Shop gesetzt wird. „Kein Push" je Ereignis wählbar.',
  grundpreis: 'Ordnet Vario-Einheiten den Gambio-Einheiten zu: Grundpreis-Einheit (VPE, für die PAngV-Grundpreisanzeige z. B. „€/100 g") und Mengeneinheit (Verkaufseinheit, aus Vario productUnit). Beide Kataloge sind in Gambio getrennt. Anlegen, Umbenennen und Löschen wirkt immer auf den Gambio-Katalog — Vario-Einheiten werden hier nur zugeordnet.',
  hersteller: 'Ordnet Gambio-Hersteller der passenden VARIO-Geschäftsbeziehung zu. Löst Namensabweichungen auf und verhindert doppelt angelegte Hersteller beim Produktimport.',
  herstellerGpsr: 'GPSR-Herstellerangaben (Hersteller + EU-Bevollmächtigter). Der Kontaktweg (Telefon, E-Mail oder Kontaktformular) lässt sich je Rolle getrennt festlegen. Die Angaben werden additiv in die Beschreibung eingefügt und nie überschrieben.',
  produkttyp: 'Ordnet VARIO-Werte dem Gambio-Produkttyp zu (VARIO → Gambio). Wird beim automatischen Export gesetzt und lässt sich je Artikel überschreiben.',
  lieferstatus: 'Pflegt den Gambio-Lieferstatus-Katalog lokal (ID + Bezeichnung anlegen/ändern) und ordnet ihm VARIO-Bestandsstatus zu (VARIO → Gambio). Wird beim automatischen Export gesetzt und lässt sich je Artikel überschreiben.',
  waehrungen: 'Vergleicht die aktiven Währungen beider Systeme. Fehlt eine in VARIO, lässt sie sich hier aktivieren; fehlt sie in Gambio, führt ein Link ins Gambio-Backend.',
  artikelmapping: 'Zentrale Feld-Zuordnung für ALLE Artikel: je Feld VARIO-Feld, fester Wert oder nicht zuordnen. Steuert, welche Felder VARIO → Gambio bespielt werden. Am einzelnen Artikel überschreibbar.',
  zusatzfelder: 'Verwaltet die Zusatzfeld-Definitionen (z. B. Nährwerte, frei definierbare Felder): anlegen, umbenennen, löschen. Die Übernahme nach VARIO erfolgt automatisch.',
  ablaeufe: 'Übersicht aller automatischen Synchronisationsläufe mit ihrem Rhythmus (z. B. „alle 5 Minuten", „stündlich"). Kein manuelles Anstoßen nötig.',
};

function panelHeadingBadge(id: SectionId): string {
  const specific = SECTION_INFO[id];
  if (specific) return infoBadge(specific);
  if (MAPPING_SECTIONS.has(id)) {
    return infoBadge('Links steht der VARIO-Wert, rechts der zugehörige Gambio-Wert. So werden z. B. Zahlungs- oder Versandarten übersetzt.');
  }
  return '';
}

/**
 * „Ungespeichert"-Zustand des aktiven Einstellungs-Abschnitts (Testfall 12.12).
 *
 * Nur Abschnitte MIT Speichern-Knopf koennen Eingaben verlieren — die Zuordnungs-Abschnitte
 * speichern jede Auswahl sofort. Erkannt wird der Speichern-Knopf an DREI Merkmalen, weil die
 * Abschnitte historisch verschieden gebaut sind: Attribut `data-save` (z. B. die Zeilen-Knoepfe
 * unter „Einheiten"), `save` irgendwo in der id (`go-b-save`, `gaf-admin-save-all`, `cat-save`)
 * oder die Aufschrift „Speichern" (auch „Zuordnung speichern"). Ein Klick auf so einen Knopf
 * loescht den Marker (der Anwender hat gespeichert; schlaegt es fehl, sagt es der Toast).
 */
let panelDirty = false;

/** Ist dieser Knopf ein Speichern-Knopf? Siehe Kommentar an `panelDirty`. */
function isSaveButton(el: HTMLElement): boolean {
  return el.hasAttribute('data-save')
    || /save/i.test(el.id)
    || /speichern/i.test(el.textContent ?? '');
}


/** Haengt die Dirty-Erkennung EINMAL an den Panel-Container (delegiert, ueberlebt jedes Rerender). */
function wireDirtyTracking(body: HTMLElement): void {
  if (body.dataset['dirtyWired'] === '1') return;
  body.dataset['dirtyWired'] = '1';
  const hasSaveButton = (): boolean =>
    Array.from(body.querySelectorAll<HTMLElement>('button')).some(isSaveButton);
  const mark = (): void => { if (hasSaveButton()) panelDirty = true; };
  body.addEventListener('input', mark);
  body.addEventListener('change', mark);
  body.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement | null)?.closest<HTMLElement>('button');
    if (btn && isSaveButton(btn)) panelDirty = false;
  });
}

/**
 * Abschnittswechsel mit Warnung. `apply` wird sofort ausgefuehrt, wenn nichts offen ist, sonst
 * erst nach „Verwerfen & wechseln" im gemeinsamen Modal (kein Browser-Dialog).
 */
function guardedSectionSwitch(apply: () => void): void {
  if (!panelDirty) { apply(); return; }
  confirmDiscardChanges(() => { panelDirty = false; apply(); }, 'Bereich');
}

/**
 * Rote Umrandung an jedem Zuordnungs-Feld, das noch leer ist. Bewusst EINE Stelle statt einer
 * Sonderbehandlung in jedem der zwölf Zuordnungs-Abschnitte: markiert werden ausschliesslich
 * Auswahlfelder in Zuordnungstabellen (`.maptable`), damit gewöhnliche Formularfelder — in denen
 * „leer“ ein gültiger Wert ist — unberührt bleiben.
 */
function markUnmappedFields(body: HTMLElement): void {
  body.querySelectorAll<HTMLSelectElement>('.maptable select.sel').forEach((sel) => {
    sel.classList.toggle('sel--unmapped', sel.value === '');
  });
  if (body.dataset['unmappedWired'] === '1') return;
  body.dataset['unmappedWired'] = '1';
  body.addEventListener('change', (ev) => {
    const sel = ev.target as HTMLElement | null;
    if (!(sel instanceof HTMLSelectElement) || !sel.closest('.maptable')) return;
    sel.classList.toggle('sel--unmapped', sel.value === '');
  });
}

async function renderPanel(host: HTMLElement, sctx: SettingsCtx): Promise<void> {
  await renderPanelSection(host, sctx);
  markUnmappedFields(host.querySelector<HTMLElement>('#go-pbody')!);
}

async function renderPanelSection(host: HTMLElement, sctx: SettingsCtx): Promise<void> {
  lastCtx = sctx;
  const head = host.querySelector<HTMLElement>('#go-phead')!;
  const body = host.querySelector<HTMLElement>('#go-pbody')!;
  // Frisch gerendertes Panel = keine ungespeicherten Eingaben (Abschnitts-Wechsel-Guard).
  panelDirty = false;
  wireDirtyTracking(body);
  const item = NAV.flatMap((g) => g.items).find((i) => i.id === active)!;
  head.innerHTML = `<span class="ph-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">${item.icon}</svg></span>
    <div><div class="pt">${item.label} ${panelHeadingBadge(active)}</div><div class="ps" id="go-psub"></div></div>`;
  // Panel-Renderer folgen in Task 5-8; vorerst Platzhalter pro Sektion.
  if (active === 'api') { await viewApi(body, sctx); return; }
  if (active === 'lager') { await viewLager(body, sctx); return; }
  if (active === 'import') { await viewImport(body, sctx); return; }
  if (active === 'anlagepruefung') { await viewAnlagepruefung(body, sctx); return; }
  if (active === 'darstellung') { await viewDarstellung(body, sctx); return; }
  if (active === 'betrieb') { await viewBetrieb(body, sctx); return; }
  if (active === 'kundenexport') { await viewKundenexport(body, sctx); return; }
  if (active === 'kundengruppen') { await viewKundengruppen(body, sctx); return; }
  if (active === 'kategorien') { await viewKategorien(body, sctx); return; }
  if (active === 'varianten') { await viewVarianten(body, sctx); return; }
  if (active === 'optionen') { await viewOptionen(body, sctx); return; }
  if (active === 'laender') { await viewLaender(body, sctx); return; }
  if (active === 'zahlungsarten') { await viewModalityMapping(body, sctx, 'payment'); return; }
  if (active === 'versandarten') { await viewModalityMapping(body, sctx, 'shipping'); return; }
  if (active === 'paketdienste') { await viewCarrierMapping(body, sctx); return; }
  if (active === 'steuerklassen') { await viewTaxClassMapping(body, sctx); return; }
  if (active === 'orderstatus') { await viewOrderStatusMapping(body, sctx); return; }
  if (active === 'grundpreis') { await viewBasePriceUnitMapping(body, sctx); return; }
  if (active === 'hersteller') { await viewHerstellerMapping(body, sctx, DEFAULT_SHOP); return; }
  if (active === 'herstellerGpsr') { await viewHerstellerGpsr(body, sctx); return; }
  if (active === 'produkttyp') { await viewProductTypeMapping(body, sctx, DEFAULT_SHOP); return; }
  if (active === 'lieferstatus') { await viewShippingStatusMapping(body, sctx, DEFAULT_SHOP); return; }
  if (active === 'waehrungen') { await viewWaehrungen(body, sctx); return; }
  if (active === 'artikelmapping') { await viewArtikelMapping(body, sctx, DEFAULT_SHOP); return; }
  if (active === 'zusatzfelder') { await viewZusatzfelder(body, sctx); return; }
  if (active === 'ablaeufe') { await viewAblaeufe(body, sctx); return; }
  body.innerHTML = `<div class="hint">Sektion „${escHtml(item.label)}" - folgt.</div>`;
}
